"use client"
import React, { useEffect, useState, useCallback, useMemo } from 'react'
import { ModalWrapper } from '../wrapperComponents/page'
import formatMsg from '../../util/formatMsg'
import Helper from '../../util/helper'
import { useDispatch, useSelector } from 'react-redux'
import { rescheduleBooking } from '../../redux/accountPackage/reducer'
import { getRescheduleData } from '../../redux/package/reducer'
import { notification } from '../../util/notification'
import { timeutil } from 'sharedresources'
import { getInstanceClone } from '../../util/timeutil'
import { CheckOutlined, DeleteOutlined, CalendarOutlined, ClockCircleOutlined, ExclamationCircleOutlined, CloseOutlined, EditOutlined } from '@ant-design/icons'
import { Button, Row, Col, Skeleton, DatePicker } from 'antd'
import { DayPicker } from 'react-day-picker'
import "./style.css"
import BookingCalendar from './BookingCalendar'
import { Moment } from 'moment'
import moment from "moment-timezone";
import { accountPackageActions, packageActions, permissions, godMode } from '../..'
import { initialState as initialAccountPackageReducer } from '../../redux/accountPackage/reducer';
import { initialState as initialPackageReducer } from '../../redux/package/reducer';
import { limitWindows, PERIOD_COUNTING } from '../../util/limitWindows';
import { sessionLimitUnitOf } from '../../util/allowanceBlocks';

const formatTime = (epoch: number) => timeutil.getInstance(epoch).format("h:mm A");

interface BookedSlot {
    id: string;
    date: number; // epoch
    day: string;
    startTime: number;
    endTime: number;
    activityId?: string;
    slotRandomId?: string;
    randomId?: string;
}

interface BookSlotsModalProps {
    open: boolean | string | null; // Can be boolean, bookingId string, or null (like RescheduleBookingModal)
    setOpen: any;
    totalClasses: number;
    bookedClasses: BookedSlot[];
    remainingCredits: number;
    packageId?: string;
    readOnly?: boolean;
    bookingPeriod?: {
        startDate: number;
        endDate: number;
    };
    selectedBatch?: any;
    selectedPricingPack?: any;
    groupedTimings?: any;
    bookedSlots?: any[];
    originalBookedTimings?: Record<string, Record<string, string[]>>;
    onSave?: (allBookings: BookedSlot[]) => void;
}

const BookSlotsModal: React.FC<BookSlotsModalProps> = ({
    open,
    setOpen,
    totalClasses,
    bookedClasses: initialBookedClasses,
    remainingCredits,
    packageId,
    readOnly = false,
    bookingPeriod,
    selectedBatch: propSelectedBatch,
    selectedPricingPack: propSelectedPricingPack,
    groupedTimings: propGroupedTimings,
    bookedSlots: propBookedSlots = [],
    originalBookedTimings = {},
    onSave
}) => {
    const dispatch = useDispatch();
    // Read from the store rather than storage so that taking control while this
    // modal is open re-enables past dates immediately, instead of waiting for some
    // unrelated dependency to change.
    const godModeMode = useSelector((state: any) => state.godMode?.session?.mode);
    const { accountPackageReducer, packageReducer, firebase } = Helper.getSelectorState(["accountPackageReducer", "packageReducer", "firebase"]);

    const [bookedSlots, setBookedSlots] = useState<BookedSlot[]>(initialBookedClasses);
    const [newSelections, setNewSelections] = useState<BookedSlot[]>([]);
    const [selectedTimings, setSelectedTimings] = useState<Record<string, Record<string, string | string[]>>>({});
    const [selectedDays, setSelectedDays] = useState<Date[]>([]);
    const [selectedBatch, setSelectedBatch] = useState<any>(propSelectedBatch || {});
    const [selectedPricingPack, setSelectedPricingPack] = useState<any>(propSelectedPricingPack || {});
    const [groupedTimings, setGroupedTimings] = useState<any>(propGroupedTimings || {});
    const [selectedMonths, setSelectedMonths] = useState<Moment[]>([]);
    const [currentMonth, setCurrentMonth] = useState(timeutil.getInstance());
    const [selectedWeeks, setSelectedWeeks] = useState<string[]>([]);
    const [selectedWeekDays, setSelectedWeekDays] = useState<any[]>([]);
    const [packageActivityMap, setPackageActivityMap] = useState(new Map());
    const [isLoading, setIsLoading] = useState(false);
    const [isEditingWindow, setIsEditingWindow] = useState(false);
    const [editableWindowStart, setEditableWindowStart] = useState<Date | null>(null);
    const [editableWindowEnd, setEditableWindowEnd] = useState<Date | null>(null);
    const [hasManuallyEditedWindow, setHasManuallyEditedWindow] = useState(false);

    useEffect(() => {
        return () => {
            dispatch(accountPackageActions.sliceOperation(initialAccountPackageReducer));
            dispatch(packageActions.sagaSuccess(initialPackageReducer));
        };
    }, []);

    useEffect(() => {
        let id = Helper.getUrlParam("id");
        if (packageReducer.rescheduleData.centerId && selectedBatch.id) {
            !readOnly && dispatch(accountPackageActions.getAvailabilityMapForSlots({ firebase, reqObj: { batchId: selectedBatch.id, centerId: packageReducer.rescheduleData.centerId, packageId: packageId } }));
        }

    }, [selectedBatch, packageReducer.rescheduleData.centerId])

    // Fetch reschedule data when modal opens (open is the bookingId, like RescheduleBookingModal)
    useEffect(() => {
        if (open) {
            // open can be boolean true, bookingId string, or null
            // If it's a string, use it as bookingId; if boolean true, use packageId as bookingId
            const bookingId = typeof open === 'string' ? open : (open === true && packageId ? packageId : null);
            if (bookingId) {
                setIsLoading(true);
                dispatch(getRescheduleData({ firebase, bookingId }));
            }
            // Reset editable window state when modal opens
            setHasManuallyEditedWindow(false);

            // Priority 1: Check rescheduleData (from API - saved booking dates)
            if (packageReducer.rescheduleData?.startDate && packageReducer.rescheduleData?.endDate) {
                const startDate = Helper.convertToDate(timeutil.getInstance(packageReducer.rescheduleData.startDate), firebase);
                const endDate = Helper.convertToDate(timeutil.getInstance(packageReducer.rescheduleData.endDate), firebase);
                setEditableWindowStart(startDate);
                setEditableWindowEnd(endDate);
            }
            // Priority 2: Check bookingPeriod prop (saved booking dates from database)
            else if (bookingPeriod?.startDate && bookingPeriod?.endDate) {
                const startDate = Helper.convertToDate(timeutil.getInstance(bookingPeriod.startDate), firebase);
                const endDate = Helper.convertToDate(timeutil.getInstance(bookingPeriod.endDate), firebase);
                setEditableWindowStart(startDate);
                setEditableWindowEnd(endDate);
            }
            // Priority 3: Fallback to batch dates only if no saved booking dates exist
            else if (selectedBatch?.startDate && selectedBatch?.endDate) {
                const startDate = Helper.convertToDate(timeutil.getInstance(selectedBatch.startDate), firebase);
                const endDate = Helper.convertToDate(timeutil.getInstance(selectedBatch.endDate), firebase);
                setEditableWindowStart(startDate);
                setEditableWindowEnd(endDate);
            } else {
                setEditableWindowStart(null);
                setEditableWindowEnd(null);
            }
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [open, packageId, bookingPeriod?.startDate, bookingPeriod?.endDate, packageReducer.rescheduleData?.startDate, packageReducer.rescheduleData?.endDate]);

    // Initialize editable dates from rescheduleData or bookingPeriod when they become available
    // Window dates come ONLY from DB (rescheduleData/bookingPeriod) or batch dates as fallback
    // Window dates are NOT recalculated when slots are selected - they remain fixed from DB
    // User can only modify them via the date pickers at the top
    useEffect(() => {
        if (hasManuallyEditedWindow) {
            return; // Don't override if user has manually edited
        }

        // Priority 1: Check rescheduleData for startDate and endDate (from API - saved dates)
        if (packageReducer.rescheduleData?.startDate && packageReducer.rescheduleData?.endDate) {
            const startDate = Helper.convertToDate(timeutil.getInstance(packageReducer.rescheduleData.startDate), firebase);
            const endDate = Helper.convertToDate(timeutil.getInstance(packageReducer.rescheduleData.endDate), firebase);
            setEditableWindowStart(startDate);
            setEditableWindowEnd(endDate);
            return;
        }

        // Priority 2: Check bookingPeriod prop (saved dates from database)
        if (bookingPeriod?.startDate && bookingPeriod?.endDate) {
            const startDate = Helper.convertToDate(timeutil.getInstance(bookingPeriod.startDate), firebase);
            const endDate = Helper.convertToDate(timeutil.getInstance(bookingPeriod.endDate), firebase);
            setEditableWindowStart(startDate);
            setEditableWindowEnd(endDate);
            return;
        }

        // Priority 3: Fallback to batch dates only if no saved booking dates exist AND dates are not set
        if ((!editableWindowStart || !editableWindowEnd) && selectedBatch?.startDate && selectedBatch?.endDate) {
            setEditableWindowStart(Helper.convertToDate(timeutil.getInstance(selectedBatch.startDate), firebase));
            setEditableWindowEnd(Helper.convertToDate(timeutil.getInstance(selectedBatch.endDate), firebase));
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [packageReducer.rescheduleData?.startDate, packageReducer.rescheduleData?.endDate, bookingPeriod?.startDate, bookingPeriod?.endDate, hasManuallyEditedWindow, selectedBatch?.startDate, selectedBatch?.endDate]);

    // Handle window date changes - filter out selected days outside the range
    const handleWindowDateChange = (startDate: Date | null, endDate: Date | null) => {
        if (!startDate || !endDate) return;

        setEditableWindowStart(startDate);
        setEditableWindowEnd(endDate);
        setHasManuallyEditedWindow(true);

        // Filter selectedDays to remove dates outside the new window range
        const startEpoch = timeutil.getEpoch(timeutil.getInstance(startDate).startOf("day"));
        const endEpoch = timeutil.getEpoch(timeutil.getInstance(endDate).endOf("day"));

        const filteredDays = selectedDays.filter(day => {
            const dayEpoch = timeutil.getEpoch(timeutil.getInstance(day).startOf("day"));
            return dayEpoch >= startEpoch && dayEpoch <= endEpoch;
        });

        if (filteredDays.length !== selectedDays.length) {
            setSelectedDays(filteredDays);
            // Also clear timings for removed dates
            const filteredDayEpochs = new Set(filteredDays.map(d => timeutil.getEpoch(timeutil.getInstance(d).startOf("day"))));
            setSelectedTimings(prev => {
                const next = { ...prev };
                Object.keys(next).forEach(activityId => {
                    if (next[activityId]) {
                        const activitySelection = { ...next[activityId] };
                        Object.keys(activitySelection).forEach(dayOrDate => {
                            // Check if this day/date is outside the window
                            // For recurring, dayOrDate is day name; for non-recurring, it's a date string
                            const isRecurring = selectedBatch?.type === "recurring";
                            if (!isRecurring) {
                                // Try to parse the date string and check if it's in range
                                try {
                                    const dateInstance = timeutil.getInstance(dayOrDate);
                                    const dayEpoch = timeutil.getEpoch(dateInstance.startOf("day"));
                                    if (dayEpoch < startEpoch || dayEpoch > endEpoch) {
                                        delete activitySelection[dayOrDate];
                                    }
                                } catch (e) {
                                    // If parsing fails, keep it (might be "Common slots" or other special key)
                                }
                            }
                        });
                        next[activityId] = activitySelection;
                    }
                });
                return next;
            });
        }
    };

    const getWeekLabel = (date: Date | Moment) => {
        const day = timeutil.getInstance(date);
        const startOfWeek = timeutil.getStartOfWeek(day, selectedBatch?.weekStartDay);
        return `Week+${timeutil.getEpoch(startOfWeek)}`;
    };

    const processBatchTimings = (batch: any) => {
        const activityGroups: any = {};

        batch.intervals?.forEach((interval: any) => {
            const activityKey = interval.activityId ?? 'default';
            if (!activityGroups[activityKey]) {
                activityGroups[activityKey] = {};
            }
            const groupTimings = activityGroups[activityKey];

            const slotKey =
                batch.type === 'recurring'
                    ? interval.day
                    : timeutil
                        .getInstance(interval.date)
                        .format(Helper.dateFormat(firebase));

            if (!groupTimings[slotKey]) {
                groupTimings[slotKey] = [];
            }
            groupTimings[slotKey].push({
                key: interval.randomId,
                label: `${formatTime(interval.startTime)} - ${formatTime(interval.endTime)}`,
            });
        });
        const mergedByActivity =
            Object.fromEntries(
                Object.entries(activityGroups).map(([activity, slotsByDay]: any) => [
                    activity,
                    Helper.mergeCommonSlots(slotsByDay, batch.sameTimings),
                ])
            );

        // place default one's at last place
        const def = mergedByActivity['default'];
        delete mergedByActivity['default'];
        if (def) {
            mergedByActivity.default = def;
        }

        return mergedByActivity;
    };

    const disabled: any = useCallback((date: Date, tempPack?: any) => {
        // Always use the latest editableWindowStart and editableWindowEnd from state
        // This ensures we get the updated values even if the function was created earlier
        const currentWindowStart = editableWindowStart;
        const currentWindowEnd = editableWindowEnd;

        const selectedPack = tempPack || selectedPricingPack?.pricingPack;
        const time = timeutil.getEpoch(timeutil.getInstance(date).endOf("day"));
        const month = timeutil.getEpoch(timeutil.getInstance(date).startOf("month"));

        // Allow selection of dates even if they have booked slots
        // Users can add additional slots to dates that already have bookings

        if (selectedBatch?.excludedDates?.map((d: any) => timeutil.getEpoch(timeutil.getInstance(d).endOf("day"))).includes(time)) {
            return true;
        }

        // For CUSTOM with DURATION (both unlimited and limited classes), restrict dates to the window
        if (selectedPack?.frequency === "CUSTOM" &&
            selectedPack?.durationType === "DURATION" &&
            selectedPack?.durationValue &&
            selectedPack?.durationUnit) {

            // Always use editable window dates if available (highest priority - user may have edited them)
            let windowStartDate: Date | null = null;
            let windowEndDate: Date | null = null;

            if (currentWindowStart && currentWindowEnd) {
                // Use editable window dates (this is the source of truth - either from calculation or user edit)
                windowStartDate = currentWindowStart;
                windowEndDate = currentWindowEnd;
            } else if (packageReducer.rescheduleData?.startDate && packageReducer.rescheduleData?.endDate) {
                // Use rescheduleData from API
                windowStartDate = Helper.convertToDate(timeutil.getInstance(packageReducer.rescheduleData.startDate), firebase);
                windowEndDate = Helper.convertToDate(timeutil.getInstance(packageReducer.rescheduleData.endDate), firebase);
            } else if (bookingPeriod?.startDate && bookingPeriod?.endDate) {
                // Use bookingPeriod prop
                windowStartDate = Helper.convertToDate(timeutil.getInstance(bookingPeriod.startDate), firebase);
                windowEndDate = Helper.convertToDate(timeutil.getInstance(bookingPeriod.endDate), firebase);
            } else if (selectedBatch?.startDate && selectedBatch?.endDate) {
                // Fall back to batch dates (for initial state when no slots selected)
                windowStartDate = Helper.convertToDate(timeutil.getInstance(selectedBatch.startDate), firebase);
                windowEndDate = Helper.convertToDate(timeutil.getInstance(selectedBatch.endDate), firebase);
            }

            // Apply window restriction if we have window dates
            if (windowStartDate && windowEndDate) {
                const windowStartEpoch = timeutil.getEpoch(timeutil.getInstance(windowStartDate).startOf("day"));
                const windowEndEpoch = timeutil.getEpoch(timeutil.getInstance(windowEndDate).endOf("day"));
                const dateEpoch = timeutil.getEpoch(timeutil.getInstance(date).startOf("day"));

                // Enable dates within the window, disable dates outside
                if (dateEpoch < windowStartEpoch || dateEpoch > windowEndEpoch) {
                    return true;
                }
            }
        }

        if (selectedPack?.frequency === "MONTH" && selectedPack?.frequencyType && !selectedPack.all) {
            const isMonthSelected = selectedMonths.some((selected) =>
                timeutil.getInstance(selected).startOf("month").isSame(month)
            );
            if (!isMonthSelected) return true;
        }

        // A God Mode operator who has taken control may book into the past, so a
        // client can have history corrected without an admin editing the database.
        // Everyone else keeps the existing rule. See canBackdateBookings().
        const isPastAndBlocked =
            timeutil.isBefore(time, timeutil.getInstance(), "day", false) &&
            !godMode.canBackdateBookings();

        if (selectedBatch?.type === "recurring") {
            let start = selectedBatch.startDate;
            let end = selectedBatch.endDate;
            if (selectedPack?.frequency === "CUSTOM") {
                if (selectedPack?.startDate) start = Math.max(start, selectedPack?.startDate);
                if (selectedPack?.endDate) end = Math.min(end, selectedPack?.endDate);
            }
            const validWeekdays = ["SUNDAY", "MONDAY", "TUESDAY", "WEDNESDAY", "THURSDAY", "FRIDAY", "SATURDAY"].filter((w: string) =>
                selectedBatch.intervals?.some((interval: any) => interval.day === w)
            );
            const dayOfWeek = timeutil.getInstance(date).format("dddd").toUpperCase();
            return time < start || time > end || !validWeekdays.includes(dayOfWeek) || isPastAndBlocked;
        } else {
            const intervalDates = selectedBatch?.intervals?.map((interval: any) => timeutil.getEpoch(timeutil.getInstance(interval.date).endOf("day"))) || [];
            return !intervalDates.includes(time) || isPastAndBlocked;
        }
    }, [editableWindowStart, editableWindowEnd, selectedPricingPack, selectedBatch, bookingPeriod, packageReducer.rescheduleData, selectedMonths, godModeMode]);

    // Get booked dates (dates that have booked slots)
    const getBookedDates = (): Set<number> => {
        const bookedDatesSet = new Set<number>();
        bookedSlots.forEach(slot => {
            const dayEpoch = timeutil.getEpoch(timeutil.getInstance(slot.date).startOf("day"));
            bookedDatesSet.add(dayEpoch);
        });
        return bookedDatesSet;
    };

    // Get newly selected dates (dates in selectedDays but not in bookedSlots)
    const getNewlySelectedDates = (): Date[] => {
        const bookedDatesSet = getBookedDates();
        return selectedDays.filter(date => {
            const dateEpoch = timeutil.getEpoch(timeutil.getInstance(date).startOf("day"));
            return !bookedDatesSet.has(dateEpoch);
        });
    };

    // Helper function to check if a day should be shown in timings (for all selected dates)
    const getFilteredDays = (dayOrDate: string) => {
        if (!selectedDays?.length) return false;
        const isRecurring = selectedBatch?.type === "recurring";
        return selectedDays.some((item) => {
            if (isRecurring) {
                const dayName = timeutil.inFormat(item, "dddd").toUpperCase();
                return dayOrDate.toUpperCase() === dayName;
            } else {
                const dayStr = timeutil.getInstance(item).format(Helper.dateFormat(firebase));
                return dayStr === dayOrDate;
            }
        });
    };

    useEffect(() => {
        if (accountPackageReducer.operationType === "BOOKING_RESCHEDULED") {
            notification("success", formatMsg("slotsBookedSuccessfully"));
            setOpen(false);
            dispatch(accountPackageActions.sliceOperation({ operationType: undefined }));
        }
    }, [accountPackageReducer.operationType]);

    // Process reschedule data when it's available
    useEffect(() => {
        const isOpen = typeof open === 'boolean' ? open : !!open;
        if (!packageReducer.rescheduleData || !isOpen) return;

        setIsLoading(false);
        let tempPackage;
        let mergeSlots;

        if (packageReducer.rescheduleData.batch) {
            tempPackage = packageReducer.rescheduleData.batch;
            mergeSlots = processBatchTimings(tempPackage);
            setSelectedBatch(tempPackage);
            setGroupedTimings(processBatchTimings(tempPackage));
        }

        if (packageReducer.rescheduleData.pricePack) {
            setSelectedPricingPack({ pricingPack: packageReducer.rescheduleData.pricePack });
        }

        if (packageReducer.rescheduleData.slots) {
            // Convert slots to BookedSlot format and deduplicate by date + slotRandomId/randomId
            const slotsMap = new Map<string, BookedSlot>();
            packageReducer.rescheduleData.slots.forEach((slot: any, index: number) => {
                const slotId = slot.slotRandomId || slot.randomId || `slot-${index}`;
                const dateEpoch = timeutil.getEpoch(timeutil.getInstance(slot.date).startOf("day"));
                // Create unique key: date + slotId + startTime + endTime
                const uniqueKey = `${dateEpoch}-${slotId}-${slot.startTime}-${slot.endTime}`;

                // Only add if not already in map (deduplicate)
                if (!slotsMap.has(uniqueKey)) {
                    slotsMap.set(uniqueKey, {
                        id: slotId,
                        date: slot.date,
                        day: timeutil.getInstance(slot.date).format("dddd"),
                        startTime: slot.startTime,
                        endTime: slot.endTime,
                        activityId: slot.activityId,
                        slotRandomId: slot.slotRandomId,
                        randomId: slot.randomId
                    });
                }
            });
            const bookedSlotsData = Array.from(slotsMap.values());
            setBookedSlots(bookedSlotsData);

            // Extract unique booked dates
            const bookedDatesSet = new Set<number>();
            packageReducer.rescheduleData.slots.forEach((slot: any) => {
                const dayEpoch = timeutil.getEpoch(timeutil.getInstance(slot.date).startOf("day"));
                bookedDatesSet.add(dayEpoch);
            });
            // Don't pre-select booked dates in calendar - calendar is only for selecting NEW dates
            // Booked dates will be shown in "Previously Booked" section, not in the calendar
            setSelectedDays([]);

            // Don't initialize selectedTimings from booked slots - this is for selecting NEW slots only
            // Keep selectedTimings empty so user can manually select timings for new dates
            const updatedMonths = new Set<number>();
            const updatedWeeks = new Set<string>();
            const updatedWeekDays = new Set<string>();

            for (const { date } of packageReducer.rescheduleData.slots) {
                const day = timeutil.getInstance(date);
                const formattedMonth = timeutil.getEpoch(getInstanceClone(day).startOf("month"));
                const weekLabel = getWeekLabel(day);

                updatedWeeks.add(weekLabel);
                updatedMonths.add(formattedMonth);
                updatedWeekDays.add(day.format("dddd").toUpperCase());
            }

            setSelectedMonths(Array.from(updatedMonths).map(d => timeutil.getInstance(d)));
            setSelectedWeeks(Array.from(updatedWeeks));

            // For CUSTOM with DURATION, add all weekdays
            let finalWeekDays = Array.from(updatedWeekDays);
            if (packageReducer.rescheduleData?.pricePack?.frequency === "CUSTOM" &&
                packageReducer.rescheduleData?.pricePack?.durationType === "DURATION") {
                const allWeekdays = ["MONDAY", "TUESDAY", "WEDNESDAY", "THURSDAY", "FRIDAY", "SATURDAY", "SUNDAY"];
                const weekDaysSet = new Set([...finalWeekDays, ...allWeekdays]);
                finalWeekDays = Array.from(weekDaysSet);
            }
            setSelectedWeekDays(finalWeekDays);

            // Set current month to first booked date or today
            if (bookedSlotsData.length > 0) {
                const startDate = timeutil.getInstance(packageReducer.rescheduleData.slots[0].date);
                let date = moment.max(startDate, timeutil.getInstance());
                const endDate = timeutil.getInstance(packageReducer.rescheduleData.slots[packageReducer.rescheduleData.slots.length - 1].date);
                date = date.isAfter(endDate) ? startDate : date;
                setCurrentMonth(date);
            }

            // Don't pre-populate selectedTimings - start empty for new selections
            setSelectedTimings({});
        }

        if (packageReducer.rescheduleData.packageActivity) {
            const tempPackageActivityMap = new Map();
            packageReducer.rescheduleData.packageActivity?.forEach((act: any) => {
                tempPackageActivityMap.set(act.id, act);
            });
            setPackageActivityMap(tempPackageActivityMap);
        }
    }, [packageReducer.rescheduleData, open]);
    const totalBooked = bookedSlots.length;
    const totalSelected = newSelections.length;

    // Calculate next upcoming slot key (earliest future slot)
    const nextUpcomingSlotKey = useMemo(() => {
        const todayEpoch = timeutil.getEpoch(timeutil.getInstance().startOf("day"));
        const allSlots = [...newSelections, ...bookedSlots];
        const futureSlots = allSlots.filter(slot => {
            const slotDateEpoch = timeutil.getEpoch(timeutil.getInstance(slot.date).startOf("day"));
            return slotDateEpoch > todayEpoch;
        });

        if (futureSlots.length === 0) return null;

        // Find the earliest future slot (by date, then by startTime)
        const nextUpcomingSlot = futureSlots.reduce((earliest, current) => {
            const earliestDateEpoch = timeutil.getEpoch(timeutil.getInstance(earliest.date).startOf("day"));
            const currentDateEpoch = timeutil.getEpoch(timeutil.getInstance(current.date).startOf("day"));

            if (currentDateEpoch < earliestDateEpoch) {
                return current;
            } else if (currentDateEpoch === earliestDateEpoch) {
                // If same date, compare by startTime
                const earliestTime = earliest.startTime || '';
                const currentTime = current.startTime || '';
                return currentTime < earliestTime ? current : earliest;
            }
            return earliest;
        });

        return `${timeutil.getEpoch(timeutil.getInstance(nextUpcomingSlot.date).startOf("day"))}-${nextUpcomingSlot.slotRandomId || nextUpcomingSlot.randomId || nextUpcomingSlot.id}-${nextUpcomingSlot.startTime}-${nextUpcomingSlot.endTime}`;
    }, [newSelections, bookedSlots]);

    // No overall cap on how many classes the membership holds: an unlimited pack, or a postpaid
    // CUSTOM pack booked under per-period counting whose limit is per day/week/month/billing
    // period -- that limit is enforced per window, and each invoice bills what was booked. Only a
    // "whole pack" limit is a total. Older bookings keep the total cap they were booked with.
    const noTotalCap = !!selectedPricingPack?.pricingPack?.unlimitedClasses || (
        packageReducer.rescheduleData?.allowanceCounting === PERIOD_COUNTING
        && selectedPricingPack?.pricingPack?.paymentType === "RECURRING"
        && selectedPricingPack?.pricingPack?.frequency === "CUSTOM"
        && selectedPricingPack?.pricingPack?.paymentTiming === "AFTER"
        && sessionLimitUnitOf(selectedPricingPack?.pricingPack) !== "PACK"
    );

    // For unlimitedClasses, don't calculate remaining slots (would be negative)
    const remainingSlots = noTotalCap
        ? undefined
        : totalClasses - totalBooked - totalSelected;

    // Helper function to estimate how many new slots would be added by selecting a timing
    // Helper function to check if a date already has slots selected (across all activities)
    const hasSlotsForDate = (dateEpoch: number, currentTimings: Record<string, Record<string, string | string[]>> = selectedTimings): boolean => {
        // Check newSelections first
        const hasInNewSelections = newSelections.some(slot => {
            const slotDateEpoch = timeutil.getEpoch(timeutil.getInstance(slot.date).startOf("day"));
            return slotDateEpoch === dateEpoch;
        });
        if (hasInNewSelections) return true;

        // Also check selectedTimings to catch slots that will be created
        const isRecurring = selectedBatch?.type === "recurring";
        const dateInstance = timeutil.getInstance(dateEpoch);
        const dayName = dateInstance.format("dddd").toUpperCase();
        const dateStr = dateInstance.format(Helper.dateFormat(firebase));

        // Check all activities
        for (const [actId, activitySelection] of Object.entries(currentTimings)) {
            if (!activitySelection) continue;

            // Check Common slots - they apply to all selected days
            if (activitySelection["Common slots"]) {
                const commonSlots = Array.isArray(activitySelection["Common slots"])
                    ? activitySelection["Common slots"]
                    : [activitySelection["Common slots"]];
                if (commonSlots.length > 0) {
                    // Check if this date is in selectedDays
                    const isDateSelected = selectedDays.some(day => {
                        const dayEpoch = timeutil.getEpoch(timeutil.getInstance(day).startOf("day"));
                        return dayEpoch === dateEpoch;
                    });
                    if (isDateSelected) return true;
                }
            }

            // Check day-specific slots
            if (isRecurring) {
                const daySlots = activitySelection[dayName];
                if (daySlots) {
                    const slots = Array.isArray(daySlots) ? daySlots : [daySlots];
                    if (slots.length > 0) {
                        // Check if this date matches the day name
                        const isDateSelected = selectedDays.some(day => {
                            const dayEpoch = timeutil.getEpoch(timeutil.getInstance(day).startOf("day"));
                            const dayInstance = timeutil.getInstance(day);
                            return dayEpoch === dateEpoch && dayInstance.format("dddd").toUpperCase() === dayName;
                        });
                        if (isDateSelected) return true;
                    }
                }
            } else {
                // For non-recurring, check all possible date format variations
                // Check exact date string match
                const dateSlots = activitySelection[dateStr];
                if (dateSlots) {
                    const slots = Array.isArray(dateSlots) ? dateSlots : [dateSlots];
                    if (slots.length > 0) return true;
                }

                // Also check all keys in activitySelection to see if any match this date
                for (const key of Object.keys(activitySelection)) {
                    if (key === "Common slots") continue;
                    // Try to parse the key as a date
                    const parsedDate = timeutil.getInstance(key, Helper.dateFormat(firebase));
                    if (parsedDate.isValid()) {
                        const keyEpoch = timeutil.getEpoch(parsedDate.startOf("day"));
                        if (keyEpoch === dateEpoch) {
                            const slots = activitySelection[key];
                            if (slots) {
                                const slotArray = Array.isArray(slots) ? slots : [slots];
                                if (slotArray.length > 0) return true;
                            }
                        }
                    }
                }
            }
        }

        return false;
    };

    // Helper function to get all dates that will be affected by selecting this timing
    const getAffectedDates = (dayOrDate: string): number[] => {
        const isRecurring = selectedBatch?.type === "recurring";
        const affectedDates: number[] = [];

        if (dayOrDate === "Common slots") {
            // For Common slots, all selected days are affected
            selectedDays.forEach(day => {
                const dateEpoch = timeutil.getEpoch(timeutil.getInstance(day).startOf("day"));
                affectedDates.push(dateEpoch);
            });
        } else if (isRecurring) {
            // For recurring, all selected days with this day name are affected
            const dayName = dayOrDate.toUpperCase();
            selectedDays.forEach(day => {
                const dayInstance = timeutil.getInstance(day);
                if (dayInstance.format("dddd").toUpperCase() === dayName) {
                    const dateEpoch = timeutil.getEpoch(dayInstance.startOf("day"));
                    affectedDates.push(dateEpoch);
                }
            });
        } else {
            // For non-recurring, only the specific date is affected
            // Parse the date string to get the epoch
            const dateInstance = timeutil.getInstance(dayOrDate, Helper.dateFormat(firebase));
            if (dateInstance.isValid()) {
                const dateEpoch = timeutil.getEpoch(dateInstance.startOf("day"));
                affectedDates.push(dateEpoch);
            }
        }

        return affectedDates;
    };

    // Helper function to check if there are booked slots for the affected dates
    const hasBookedSlotsForDates = (affectedDates: number[]): { hasBooked: boolean; bookedDate?: number } => {
        // Always check for exact date match, not day name match
        // This allows users to book different time slots for different dates even if they share the same day name
        // The "one slot per day" restriction only applies to the specific date being selected, not across all dates with the same day name

        for (const dateEpoch of affectedDates) {
            const hasBooked = bookedSlots.some(slot => {
                const slotDateEpoch = timeutil.getEpoch(timeutil.getInstance(slot.date).startOf("day"));
                // Check exact date match for both recurring and non-recurring batches
                // This ensures the restriction only applies to the specific date, not all dates with the same day name
                return slotDateEpoch === dateEpoch;
            });

            if (hasBooked) {
                return { hasBooked: true, bookedDate: dateEpoch };
            }
        }

        return { hasBooked: false };
    };

    const estimateNewSlotsForTiming = (dayOrDate: string, timingKey: string, activityId: string): number => {
        const isRecurring = selectedBatch?.type === "recurring";
        const actKey = activityId ?? "default";

        // Find the timing interval to get its details
        const timingInterval = selectedBatch?.intervals?.find((obj: any) => obj.randomId === timingKey);
        if (!timingInterval) return 0;

        const timeLabel = `${formatTime(timingInterval.startTime)} - ${formatTime(timingInterval.endTime)}`;

        // Check if this timing is being selected under "Common slots" or day-specific
        // We need to check the groupedTimings structure to see if this timing appears under "Common slots"
        const activityTimings = groupedTimings[activityId];
        const isCommonSlot = activityTimings?.["Common slots"]?.some((t: any) => t.key === timingKey);

        if (isCommonSlot) {
            // For "Common slots", the timing applies to ALL selected days
            // Count how many intervals match this timing across all selected days
            let count = 0;

            selectedDays.forEach(day => {
                const dayOrDateStr = isRecurring
                    ? timeutil.getInstance(day).format("dddd").toUpperCase()
                    : timeutil.getInstance(day).format(Helper.dateFormat(firebase));

                // Count matching intervals for this day with this time
                if (isRecurring) {
                    const matches = selectedBatch?.intervals?.filter((obj: any) => {
                        const objTime = `${formatTime(obj.startTime)} - ${formatTime(obj.endTime)}`;
                        return obj.day === dayOrDateStr && objTime === timeLabel &&
                            (actKey === "default" ? !obj.activityId : obj.activityId === actKey);
                    }) || [];
                    count += matches.length;
                } else {
                    // For specific dates, count matching intervals for this date
                    const matches = selectedBatch?.intervals?.filter((obj: any) => {
                        const objTime = `${formatTime(obj.startTime)} - ${formatTime(obj.endTime)}`;
                        const objDateStr = timeutil.getInstance(obj.date).format(Helper.dateFormat(firebase));
                        return objDateStr === dayOrDateStr && objTime === timeLabel &&
                            (actKey === "default" ? !obj.activityId : obj.activityId === actKey);
                    }) || [];
                    count += matches.length;
                }
            });

            return count;
        } else {
            // For day-specific slots, count matching days
            if (isRecurring) {
                const dayName = dayOrDate.toUpperCase();
                return selectedDays.filter(day => {
                    const dayNameFromDate = timeutil.getInstance(day).format("dddd").toUpperCase();
                    return dayNameFromDate === dayName;
                }).length;
            } else {
                const dateStr = dayOrDate;
                return selectedDays.filter(day => {
                    const dayStr = timeutil.getInstance(day).format(Helper.dateFormat(firebase));
                    return dayStr === dateStr;
                }).length;
            }
        }
    };

    // Get availability status for a slot (similar to BookingCalendar.tsx)
    const getAvailabilityPerSlot = (slot: any, activityId: string, dayOrDate?: string): { text: string; color: string } => {
        if (!selectedDays?.length) return { text: "", color: "" };
        const maxCapacity = accountPackageReducer.maxCapacityPerBatchCenter || 0;
        let availability = maxCapacity;
        const isRecurring = selectedBatch?.type === "recurring";
        const timeLabel = slot?.label;
        const actId = activityId ?? "default";
        const centerId = packageReducer.rescheduleData?.centerId;
        const batchSlotMap = accountPackageReducer.batchSlotMap;
        const allowWaitlist = accountPackageReducer.allowWaitlist;
        const waitlistCapacity = accountPackageReducer.waitlistCapacity;

        // Determine which days to check based on dayOrDate parameter
        let daysList: Date[] = [];

        if (dayOrDate === "Common slots") {
            // For Common slots, check all selected days
            daysList = selectedPricingPack?.pricingPack?.frequency == "WEEK" && selectedPricingPack?.pricingPack?.all == false
                ? selectedDays?.filter((day) => {
                    let dayName = timeutil.inFormat(day, "dddd");
                    return selectedWeekDays?.includes(dayName.toUpperCase());
                })
                : [...selectedDays];
        } else if (dayOrDate) {
            // For day-specific slots, filter to only the relevant days
            if (isRecurring) {
                const dayName = dayOrDate.toUpperCase();
                daysList = selectedDays.filter(day => {
                    const dayNameFromDate = timeutil.getInstance(day).format("dddd").toUpperCase();
                    return dayNameFromDate === dayName;
                });
            } else {
                // For non-recurring, find the date that matches dayOrDate
                const dateStr = dayOrDate;
                daysList = selectedDays.filter(day => {
                    const dayStr = timeutil.getInstance(day).format(Helper.dateFormat(firebase));
                    return dayStr === dateStr;
                });
            }
        } else {
            // Fallback: use all selected days
            daysList = selectedPricingPack?.pricingPack?.frequency == "WEEK" && selectedPricingPack?.pricingPack?.all == false
                ? selectedDays?.filter((day) => {
                    let dayName = timeutil.inFormat(day, "dddd");
                    return selectedWeekDays?.includes(dayName.toUpperCase());
                })
                : [...selectedDays];
        }

        // Calculate minimum availability across all relevant days
        daysList.forEach(day => {
            let dayOrDateStr = isRecurring
                ? timeutil.inFormat(day, "dddd").toLowerCase()
                : timeutil.getInstance(day).format(Helper.dateFormat(firebase));

            const match = selectedBatch?.intervals?.find((obj: any) => {
                const objTime = `${formatTime(obj.startTime)} - ${formatTime(obj.endTime)}`;
                const objDayOrDate = isRecurring
                    ? obj.day.toLowerCase()
                    : timeutil.getInstance(obj.date).format(Helper.dateFormat(firebase));
                return objDayOrDate === dayOrDateStr && objTime === timeLabel &&
                    (actId === "default" ? !obj.activityId : obj.activityId === actId);
            });

            if (match && selectedBatch?.id && centerId) {
                const slotKey = `${selectedBatch.id}-${match?.randomId}-${timeutil.getEpoch(day)}-${centerId}`;
                if (batchSlotMap && (batchSlotMap[slotKey] !== undefined && batchSlotMap[slotKey] !== null)) {
                    availability = Math.min(availability, batchSlotMap[slotKey]);
                }
            }
        });

        // Determine status based on availability
        if (availability === maxCapacity) {
            return { text: formatMsg("bookSlots.available"), color: "#00855A" };
        }

        const current = availability || 0;

        // Calculate percentage
        const percentage = maxCapacity > 0 ? (current / maxCapacity) * 100 : 0;

        // Determine status
        if (current <= 0) {
            if (allowWaitlist && !waitlistCapacity) {
                return {
                    text: formatMsg("bookSlots.slotsFilledWaitlist"),
                    color: "#E56C32"
                };
            } else if (allowWaitlist && waitlistCapacity) {
                if (current + waitlistCapacity <= 0) {
                    return {
                        text: formatMsg("bookSlots.soldOut"),
                        color: "#8D8D8D"
                    };
                } else {
                    return {
                        text: formatMsg("bookSlots.slotsFilledWaitlist"),
                        color: "#E56C32"
                    };
                }
            }
            return {
                text: formatMsg("bookSlots.soldOut"),
                color: "#8D8D8D"
            };
        } else if (percentage >= 50) {
            return { text: formatMsg("bookSlots.available"), color: "#00855A" };
        } else if (percentage >= 20) {
            return { text: formatMsg("bookSlots.fillingFast"), color: "#E09D00" };
        } else {
            return { text: formatMsg("bookSlots.slotsLeft", { current }), color: "#FD5F85" };
        }
    };

    // Helper function to get font color for availability badge
    const getAvailabilityFontColor = (availability: { text: string; color: string }): string => {
        // Check by color value to determine status (more reliable than text which may be localized)
        if (availability.color === "#00855A") {
            // Available - green font
            return "#00855A";
        } else if (availability.color === "#E09D00") {
            // Filling fast - orange font
            return "#E09D00";
        } else if (availability.color === "#8D8D8D") {
            // Sold out - grey font
            return "#8D8D8D";
        } else if (availability.color === "#E56C32") {
            // Slots filled waitlist - orange font
            return "#E56C32";
        } else if (availability.color === "#FD5F85") {
            // Slots left - keep original color or use a default
            return "#FD5F85";
        }
        // Default to black if unknown
        return "#000000";
    };

    const removeBookedSlot = (slot: BookedSlot) => {
        // Create unique key to identify the exact slot
        // Ensure date is normalized to epoch for consistent comparison
        const slotDate = typeof slot.date === 'number' ? slot.date : timeutil.getEpoch(timeutil.getInstance(slot.date));
        const dateEpoch = timeutil.getEpoch(timeutil.getInstance(slotDate).startOf("day"));
        const slotId = slot.slotRandomId || slot.randomId || slot.id;
        const uniqueKey = `${dateEpoch}-${slotId}-${slot.startTime}-${slot.endTime}`;

        // Remove only the specific slot using the unique key
        setBookedSlots(prev => {
            // Create a new array to ensure React detects the change
            const filtered = prev.filter(s => {
                const sDate = typeof s.date === 'number' ? s.date : timeutil.getEpoch(timeutil.getInstance(s.date));
                const sDateEpoch = timeutil.getEpoch(timeutil.getInstance(sDate).startOf("day"));
                const sSlotId = s.slotRandomId || s.randomId || s.id;
                const sUniqueKey = `${sDateEpoch}-${sSlotId}-${s.startTime}-${s.endTime}`;
                return sUniqueKey !== uniqueKey;
            });

            // Check if this date has any remaining slots, if not, remove it from selectedDays
            const remainingSlotsForDate = filtered.filter(s => {
                const sDate = typeof s.date === 'number' ? s.date : timeutil.getEpoch(timeutil.getInstance(s.date));
                const sDateEpoch = timeutil.getEpoch(timeutil.getInstance(sDate).startOf("day"));
                return sDateEpoch === dateEpoch;
            });

            if (remainingSlotsForDate.length === 0) {
                setSelectedDays(prevDays => {
                    const filteredDays = prevDays.filter(d => {
                        const dEpoch = timeutil.getEpoch(timeutil.getInstance(d).startOf("day"));
                        return dEpoch !== dateEpoch;
                    });
                    return filteredDays; // Return new array
                });
            }

            return filtered; // Return new array to trigger re-render
        });

        // Also remove from selectedTimings
        const updatedTimings = { ...selectedTimings };
        const isRecurring = selectedBatch?.type === "recurring";
        const dayOrDateKey = isRecurring
            ? timeutil.getInstance(slot.date).format("dddd").toUpperCase()
            : timeutil.getInstance(slot.date).format(Helper.dateFormat(firebase));
        const actKey = slot.activityId ?? "default";
        const randomId = slot.slotRandomId || slot.randomId;

        if (updatedTimings[actKey]?.[dayOrDateKey]) {
            const dayOrDateValue = updatedTimings[actKey][dayOrDateKey];
            if (Array.isArray(dayOrDateValue)) {
                updatedTimings[actKey][dayOrDateKey] = dayOrDateValue.filter(
                    (id: string) => id !== randomId
                );
                // Clean up empty arrays
                if (updatedTimings[actKey][dayOrDateKey].length === 0) {
                    delete updatedTimings[actKey][dayOrDateKey];
                }
            } else {
                delete updatedTimings[actKey][dayOrDateKey];
            }
            // Clean up empty activity objects
            if (Object.keys(updatedTimings[actKey]).length === 0) {
                delete updatedTimings[actKey];
            }
        }
        setSelectedTimings(updatedTimings);
    };

    // Update newSelections whenever selectedTimings or selectedDays change
    useEffect(() => {
        if (!selectedBatch?.intervals || !selectedDays?.length || !Object.keys(selectedTimings).length) {
            setNewSelections([]);
            return;
        }

        const newSlots: BookedSlot[] = [];
        const bookedSlotsMap = new Map<string, boolean>();

        // Create a map of booked slots for quick lookup
        bookedSlots.forEach(slot => {
            const dateEpoch = timeutil.getEpoch(timeutil.getInstance(slot.date).startOf("day"));
            const slotId = slot.slotRandomId || slot.randomId || slot.id;
            const key = `${dateEpoch}-${slotId}-${slot.startTime}-${slot.endTime}`;
            bookedSlotsMap.set(key, true);
        });

        const isRecurring = selectedBatch.type === "recurring";

        // Process all selected dates (including dates with existing bookings)
        // The bookedSlotsMap check will prevent adding duplicate slots
        for (const date of selectedDays) {
            const day = timeutil.getInstance(date);
            const tempDate = isRecurring
                ? day.format("dddd").toUpperCase()
                : day.format(Helper.dateFormat(firebase));

            Object.entries(selectedTimings).forEach(([actId, activitySelection]) => {
                if (!activitySelection) return;

                let randomIds: string[] = [];

                // Handle "Common slots"
                if (activitySelection["Common slots"]) {
                    const commonSlots = Array.isArray(activitySelection["Common slots"])
                        ? activitySelection["Common slots"]
                        : [activitySelection["Common slots"]];

                    commonSlots.forEach(commonSlotId => {
                        const selectedSlotObj = selectedBatch.intervals.find(
                            (obj: any) => obj.randomId === commonSlotId
                        );
                        if (!selectedSlotObj) return;

                        const timeLabel = `${formatTime(selectedSlotObj.startTime)} - ${formatTime(selectedSlotObj.endTime)}`;

                        const matches = selectedBatch.intervals.filter((obj: any) => {
                            const objTime = `${formatTime(obj.startTime)} - ${formatTime(obj.endTime)}`;
                            const objDayOrDate = isRecurring
                                ? obj.day
                                : timeutil.getInstance(obj.date).format(Helper.dateFormat(firebase));
                            return objDayOrDate === tempDate &&
                                objTime === timeLabel &&
                                (actId === "default" ? !obj.activityId : obj.activityId === actId);
                        });

                        randomIds.push(...matches.map((m: any) => m.randomId));
                    });
                } else {
                    // Handle day-specific slots
                    const dayKey = isRecurring
                        ? day.format("dddd").toUpperCase()
                        : day.format(Helper.dateFormat(firebase));

                    const sel = activitySelection[dayKey];
                    if (sel) {
                        const selArr = Array.isArray(sel) ? sel : [sel];
                        randomIds.push(...selArr);
                    }
                }

                // Create BookedSlot objects for each randomId
                for (const randomId of randomIds) {
                    if (!randomId) continue;

                    const selectedInterval = selectedBatch.intervals.find((obj: any) => obj.randomId === randomId);
                    if (!selectedInterval) continue;

                    const dateEpoch = timeutil.getEpoch(day);
                    const slotId = randomId;
                    const key = `${timeutil.getEpoch(day.startOf("day"))}-${slotId}-${selectedInterval.startTime}-${selectedInterval.endTime}`;

                    // Only add if not already booked
                    if (!bookedSlotsMap.has(key)) {
                        newSlots.push({
                            id: slotId,
                            date: dateEpoch,
                            day: day.format("dddd"),
                            startTime: selectedInterval.startTime,
                            endTime: selectedInterval.endTime,
                            activityId: selectedInterval.activityId ?? undefined,
                            slotRandomId: randomId,
                            randomId: randomId
                        });
                    }
                }
            });
        }

        // Deduplicate by date + slotRandomId/randomId + startTime + endTime
        const seenSlots = new Set<string>();
        const deduplicatedSlots = newSlots.filter(slot => {
            const dateEpoch = timeutil.getEpoch(timeutil.getInstance(slot.date).startOf("day"));
            const slotId = slot.slotRandomId || slot.randomId || slot.id;
            const uniqueKey = `${dateEpoch}-${slotId}-${slot.startTime}-${slot.endTime}`;
            if (seenSlots.has(uniqueKey)) {
                return false;
            }
            seenSlots.add(uniqueKey);
            return true;
        });

        setNewSelections(deduplicatedSlots);
    }, [selectedTimings, selectedDays, selectedBatch, bookedSlots, firebase]);

    // Auto-select single "Common slots" timing when dates are selected
    useEffect(() => {
        if (!selectedBatch?.intervals || !selectedDays?.length || readOnly) return;

        // Check each activity for "Common slots"
        Object.entries(groupedTimings).forEach(([activityId, slotsByDay]: any) => {
            if (!slotsByDay || !slotsByDay["Common slots"]) return;

            const commonSlots = Array.isArray(slotsByDay["Common slots"])
                ? slotsByDay["Common slots"]
                : [slotsByDay["Common slots"]];

            // If there's only one common slot, auto-select it
            if (commonSlots.length === 1) {
                const actKey = activityId ?? "default";
                const timingKey = commonSlots[0].key;

                setSelectedTimings(prev => {
                    const updated = { ...prev };
                    if (!updated[actKey]) {
                        updated[actKey] = {};
                    }
                    // Only auto-select if not already selected
                    const currentCommonSlots = updated[actKey]["Common slots"];
                    const currentArray = Array.isArray(currentCommonSlots)
                        ? currentCommonSlots
                        : (currentCommonSlots ? [currentCommonSlots] : []);
                    if (!currentArray.includes(timingKey)) {
                        updated[actKey]["Common slots"] = [...currentArray, timingKey];
                    }
                    return updated;
                });
            }
        });
    }, [selectedDays, groupedTimings, selectedBatch, readOnly]);
    const groupBookedSlotsByDay = () => {
        const grouped: { [key: string]: BookedSlot[] } = {};
        // Deduplicate slots by date + slotRandomId/randomId before grouping
        const seenSlots = new Set<string>();
        bookedSlots.forEach(slot => {
            const slotId = slot.slotRandomId || slot.randomId || slot.id;
            // Normalize date to epoch for consistent comparison
            const slotDate = typeof slot.date === 'number' ? slot.date : timeutil.getEpoch(timeutil.getInstance(slot.date));
            const dateEpoch = timeutil.getEpoch(timeutil.getInstance(slotDate).startOf("day"));
            const uniqueKey = `${dateEpoch}-${slotId}-${slot.startTime}-${slot.endTime}`;

            // Only add if not already seen
            if (!seenSlots.has(uniqueKey)) {
                seenSlots.add(uniqueKey);
                const dayName = timeutil.getInstance(slotDate).format("dddd");
                if (!grouped[dayName]) {
                    grouped[dayName] = [];
                }
                grouped[dayName].push(slot);
            }
        });

        // Sort slots within each day group by date, then by startTime to maintain consistent order
        Object.keys(grouped).forEach(day => {
            grouped[day].sort((a, b) => {
                const dateA = typeof a.date === 'number' ? a.date : timeutil.getEpoch(timeutil.getInstance(a.date));
                const dateB = typeof b.date === 'number' ? b.date : timeutil.getEpoch(timeutil.getInstance(b.date));
                if (dateA !== dateB) {
                    return dateA - dateB;
                }
                return a.startTime - b.startTime;
            });
        });

        return grouped;
    };

    // Helper function to get current window dates
    // Window dates come ONLY from DB (editableWindowStart/End, rescheduleData, or bookingPeriod)
    // NOT calculated from selected slots
    const getCurrentWindowDates = (): { start: Date | null; end: Date | null } => {
        // Priority 1: Use editable dates if set (user has edited or they were initialized from DB)
        if (editableWindowStart && editableWindowEnd) {
            return {
                start: editableWindowStart,
                end: editableWindowEnd
            };
        }

        // Priority 2: Check rescheduleData (from API - saved booking dates)
        if (packageReducer.rescheduleData?.startDate && packageReducer.rescheduleData?.endDate) {
            return {
                start: Helper.convertToDate(timeutil.getInstance(packageReducer.rescheduleData.startDate), firebase),
                end: Helper.convertToDate(timeutil.getInstance(packageReducer.rescheduleData.endDate), firebase)
            };
        }

        // Priority 3: Check bookingPeriod prop (saved booking dates from database)
        if (bookingPeriod?.startDate && bookingPeriod?.endDate) {
            return {
                start: Helper.convertToDate(timeutil.getInstance(bookingPeriod.startDate), firebase),
                end: Helper.convertToDate(timeutil.getInstance(bookingPeriod.endDate), firebase)
            };
        }

        // Priority 4: Fallback to batch dates
        if (selectedBatch?.startDate && selectedBatch?.endDate) {
            return {
                start: Helper.convertToDate(timeutil.getInstance(selectedBatch.startDate), firebase),
                end: Helper.convertToDate(timeutil.getInstance(selectedBatch.endDate), firebase)
            };
        }

        return { start: null, end: null };
    };

    const formatBookingPeriod = () => {
        // Window dates come ONLY from DB (editableWindowStart/End, rescheduleData, or bookingPeriod)
        // NOT calculated from selected slots

        // Priority 1: Use editable dates if set (user has edited or they were initialized from DB)
        if (editableWindowStart && editableWindowEnd) {
            const start = timeutil.getInstance(editableWindowStart).format("MMM DD, YYYY");
            const end = timeutil.getInstance(editableWindowEnd).format("MMM DD, YYYY");
            return `${start} - ${end}`;
        }

        // Priority 2: Check rescheduleData (from API - saved booking dates)
        if (packageReducer.rescheduleData?.startDate && packageReducer.rescheduleData?.endDate) {
            const start = timeutil.getInstance(packageReducer.rescheduleData.startDate).format("MMM DD, YYYY");
            const end = timeutil.getInstance(packageReducer.rescheduleData.endDate).format("MMM DD, YYYY");
            return `${start} - ${end}`;
        }

        // Priority 3: Check bookingPeriod prop (saved booking dates from database)
        if (bookingPeriod?.startDate && bookingPeriod?.endDate) {
            const start = timeutil.getInstance(bookingPeriod.startDate).format("MMM DD, YYYY");
            const end = timeutil.getInstance(bookingPeriod.endDate).format("MMM DD, YYYY");
            return `${start} - ${end}`;
        }

        // Priority 4: Fallback to batch dates
        if (selectedBatch?.startDate && selectedBatch?.endDate) {
            const start = timeutil.getInstance(selectedBatch.startDate).format("MMM DD, YYYY");
            const end = timeutil.getInstance(selectedBatch.endDate).format("MMM DD, YYYY");
            return `${start} - ${end}`;
        }

        return "";
    };

    const handleSave = () => {
        // If onSave callback is provided, use it (for parent component handling)
        if (onSave) {
            const allBookings = [...bookedSlots, ...newSelections];
            onSave(allBookings);
            setOpen(false);
            return;
        }

        // Otherwise, use the same onFinish logic from RescheduleBookingModal
        // Allow saving if there are new selections OR if slots were removed (modifications)
        // Don't require selectedDays to have length if we're just removing slots
        const hasNewSelections = newSelections.length > 0;
        const hasRemovals = bookedSlots.length < initialBookedClasses.length;
        const hasChanges = hasNewSelections || hasRemovals;

        // If no changes at all and no slots remain, show error
        if (!hasChanges && bookedSlots.length === 0) {
            notification("error", formatMsg("error.selectslots"));
            return;
        }

        // If we have new selections, we need selectedDays to be populated
        // But if we're only removing slots, selectedDays can be empty
        if (hasNewSelections && !selectedDays?.length) {
            notification("error", formatMsg("error.selectslots"));
            return;
        }

        // Check capacity if available
        const { maxCapacityPerBatchCenter, allowWaitlist, waitlistCapacity } = accountPackageReducer;
        const commonAvailability = maxCapacityPerBatchCenter || 0;
        if (commonAvailability + (waitlistCapacity || 0) <= 0) {
            notification("error", formatMsg("capacityIsFull"));
            return;
        }

        // Validate frequency constraints
        const pricingPack = selectedPricingPack?.pricingPack;
        if (!pricingPack) {
            notification("error", formatMsg("bookSlots.pricingPackNotSelected"));
            return;
        }

        const pricePackDuration = pricingPack.frequencyValue || 1;

        switch (pricingPack.frequency) {
            case "MONTH":
                if (selectedMonths?.length > pricePackDuration) {
                    notification("error", formatMsg("error.moreMonths"));
                    return;
                }
                break;
            case "WEEK":
                if (selectedWeeks?.length > pricePackDuration) {
                    notification("error", formatMsg("error.moreWeeks"));
                    return;
                }
                break;
            case "DAILY":
                // Only validate selectedDays length if we have new selections
                // If only removing slots, selectedDays can be empty
                if (hasNewSelections && selectedDays?.length > pricePackDuration) {
                    notification("error", formatMsg("error.moreDays"));
                    return;
                }
                break;
        }

        // Calculate weeks and months from selected days
        let tempWeeks: any = new Set();
        let tempMonths: any = new Set();
        selectedDays.forEach(d => {
            const tempD = timeutil.getInstance(d);
            tempWeeks.add(timeutil.getEpoch(timeutil.getStartOfWeek(tempD, selectedBatch?.weekStartDay)));
            tempMonths.add(timeutil.getEpoch(tempD.clone().startOf("month")));
        });
        tempWeeks = Array.from(tempWeeks).sort((a: any, b: any) => a - b);
        tempMonths = Array.from(tempMonths).sort((a: any, b: any) => a - b);

        // Validate atleastValue constraint
        if (selectedBatch?.atleastValue) {
            let missing = false;
            switch (selectedBatch.atleastFrequency) {
                case "days":
                    // Only validate atleastValue if we have new selections
                    // If only removing slots, skip this validation
                    if (hasNewSelections && selectedDays?.length < selectedBatch.atleastValue)
                        missing = true;
                    break;
                case "weeks":
                    if (tempWeeks?.length < selectedBatch.atleastValue)
                        missing = true;
                    break;
                case "months":
                    if (tempMonths?.length < selectedBatch.atleastValue)
                        missing = true;
                    break;
            }
            if (missing) {
                notification("error", formatMsg("error.atleast", { x: selectedBatch.atleastValue, y: selectedBatch.atleastFrequency }));
                return;
            }
        }

        // Validate consecutive constraint
        // Only validate if we have new selections (not when only removing slots)
        if (selectedBatch?.onlyConsecutive && hasNewSelections) {
            switch (selectedBatch.consecutiveFrequency) {
                case "days":
                    const sortedDays = [...selectedDays].sort((a, b) => a.getTime() - b.getTime());
                    let daysAreConsecutive = true;
                    for (let i = 1; i < sortedDays.length; i++) {
                        const prevDate = timeutil.getInstance(sortedDays[i - 1]);
                        const currentDate = timeutil.getInstance(sortedDays[i]);
                        const dayDiff = currentDate.diff(prevDate, 'day');

                        if (dayDiff === 1) continue;

                        let allDisabled = true;
                        let dayToCheck = prevDate.clone().add(1, 'day');
                        while (dayToCheck.isBefore(currentDate)) {
                            if (!disabled(dayToCheck)) {
                                allDisabled = false;
                                break;
                            }
                            dayToCheck = getInstanceClone(dayToCheck).add(1, 'day');
                        }

                        if (!allDisabled) {
                            daysAreConsecutive = false;
                            break;
                        }
                    }

                    if ((pricingPack?.frequency === "WEEK" && pricingPack?.frequencyValue < 7)) {
                        const weekDays = ["SUNDAY", "MONDAY", "TUESDAY", "WEDNESDAY", "THURSDAY", "FRIDAY", "SATURDAY"].filter(w =>
                            selectedBatch.type === "recurring" ? selectedBatch.intervals?.some((interval: any) => interval.day === w) : true
                        );
                        const indices = selectedWeekDays.map((day: string) => weekDays.indexOf(day)).sort((a, b) => a - b);
                        for (let i = 1; i < indices.length; i++) {
                            if (indices[i] !== indices[i - 1] + 1) {
                                daysAreConsecutive = indices[0] === 0 && indices[indices.length - 1] === weekDays.length - 1;
                                break;
                            }
                        }
                    }

                    if (!daysAreConsecutive) {
                        notification("error", formatMsg("consecutiveXRequired", { x: selectedBatch.consecutiveFrequency }));
                        return;
                    }
                    break;

                case "weeks":
                    const weekStartDates = selectedWeeks.map((ws: string) => {
                        const [, timestampStr] = ws.split("+");
                        return timeutil.getInstance(parseInt(timestampStr, 10));
                    });
                    weekStartDates.sort((a, b) => timeutil.getEpoch(a) - timeutil.getEpoch(b));
                    let weeksAreConsecutive = true;
                    for (let i = 1; i < weekStartDates.length; i++) {
                        if (weekStartDates[i].diff(weekStartDates[i - 1], "week") !== 1) {
                            weeksAreConsecutive = false;
                            break;
                        }
                    }
                    if (!weeksAreConsecutive) {
                        notification("error", formatMsg("consecutiveXRequired", { x: selectedBatch.consecutiveFrequency }));
                        return;
                    }
                    break;

                case "months":
                    let monthsAreConsecutive = true;
                    for (let i = 1; i < tempMonths.length; i++) {
                        if (timeutil.getInstance(tempMonths[i]).diff(timeutil.getInstance(tempMonths[i - 1]), "month") !== 1) {
                            monthsAreConsecutive = false;
                            break;
                        }
                    }
                    if (!monthsAreConsecutive) {
                        notification("error", formatMsg("consecutiveXRequired", { x: selectedBatch.consecutiveFrequency }));
                        return;
                    }
                    break;
            }
        }

        let frequencyValueError = false;

        // Validate CUSTOM with DURATION: Check if classes per window exceed noOfClasses.
        // A booking under per-period counting needs no "Pack runs for" to know its windows, so an
        // open-ended recurring pack is checked too; older bookings keep needing the duration.
        const periodCounting = packageReducer.rescheduleData?.allowanceCounting === PERIOD_COUNTING;
        if (
            pricingPack.frequency === "CUSTOM" &&
            pricingPack.durationType === "DURATION" &&
            pricingPack.noOfClasses &&
            !pricingPack.unlimitedClasses &&
            ((pricingPack.durationValue && pricingPack.durationUnit)
                || (periodCounting && pricingPack.paymentType === "RECURRING"))
        ) {
            // Helper function to calculate windows from selected days
            const calculateWindowsFromDays = (days: Date[]) => {
                const sortedDays = [...days].sort((a, b) => a.getTime() - b.getTime());
                const durationValue = pricingPack.durationValue;
                const durationUnit = pricingPack.durationUnit.toLowerCase();
                const windows: Array<{ start: Date; end: Date; days: Date[] }> = [];

                for (const day of sortedDays) {
                    const dayInstance = timeutil.getInstance(day);
                    let addedToWindow = false;

                    // Check if this day falls within any existing window
                    for (const window of windows) {
                        const windowStart = timeutil.getInstance(window.start);
                        const windowEnd = timeutil.getInstance(window.end);

                        if ((timeutil.isSame(dayInstance, windowStart, "day") || timeutil.isAfter(dayInstance, windowStart, "day")) &&
                            (timeutil.isSame(dayInstance, windowEnd, "day") || timeutil.isBefore(dayInstance, windowEnd, "day"))) {
                            window.days.push(day);
                            addedToWindow = true;
                            break;
                        }
                    }

                    // If not added to any existing window, create a new window
                    if (!addedToWindow) {
                        const windowStart = getInstanceClone(dayInstance).startOf("day");
                        let windowEnd = getInstanceClone(windowStart);

                        if (durationUnit === "months") {
                            windowEnd = windowEnd.add(durationValue, "month").endOf("day");
                        } else if (durationUnit === "weeks") {
                            windowEnd = windowEnd.add(durationValue, "week").endOf("day");
                        } else if (durationUnit === "days") {
                            windowEnd = windowEnd.add(durationValue - 1, "day").endOf("day");
                        }

                        windows.push({
                            start: Helper.convertToDate(windowStart, firebase),
                            end: Helper.convertToDate(windowEnd, firebase),
                            days: [day]
                        });
                    }
                }

                return windows;
            };

            // Helper function to count classes in a window
            const countClassesInWindow = (window: { start: Date; end: Date; days: Date[] }) => {
                const isRecurring = selectedBatch.type === "recurring";
                let totalClasses = 0;

                window.days.forEach(day => {
                    const dayOrDateStr = isRecurring
                        ? timeutil.inFormat(day, "dddd").toLowerCase()
                        : timeutil.getInstance(day).format(Helper.dateFormat(firebase));

                    Object.values(selectedTimings).forEach((activitySelection: any) => {
                        if (!activitySelection) return;

                        // Check for "Common slots"
                        const commonSlots = activitySelection["Common slots"];
                        if (commonSlots) {
                            if (Array.isArray(commonSlots)) {
                                totalClasses += commonSlots.length;
                            } else if (typeof commonSlots === "string") {
                                totalClasses += 1;
                            }
                        }

                        // Check for day-specific timings
                        const timingsForDay = activitySelection[dayOrDateStr.toUpperCase()];
                        if (timingsForDay) {
                            if (Array.isArray(timingsForDay)) {
                                totalClasses += timingsForDay.length;
                            } else if (typeof timingsForDay === "string") {
                                totalClasses += 1;
                            }
                        }
                    });
                });

                return totalClasses;
            };

            // Calculate windows and validate each window
            // Only validate if we have new selections (not when only removing slots)
            if (hasNewSelections) {
                // Per day/week/month, per billing period or for the whole pack -- the same windows
                // the backend checks. A booking made before that counting existed keeps the
                // "Pack runs for" windows it was booked under.
                const windows = limitWindows(selectedDays, pricingPack, {
                    periodCounting,
                    anchor: packageReducer.rescheduleData?.startDate ?? null,
                    legacy: calculateWindowsFromDays,
                });
                for (const window of windows) {
                    const classesCount = countClassesInWindow(window);
                    if (classesCount > pricingPack.noOfClasses) {
                        const windowStart = timeutil.getInstance(window.start);
                        const windowEnd = timeutil.getInstance(window.end);
                        notification("error", formatMsg("bookSlots.windowExceedsLimit", {
                            windowStart: windowStart.format("MMM DD, YYYY"),
                            windowEnd: windowEnd.format("MMM DD, YYYY"),
                            noOfClasses: pricingPack.noOfClasses,
                            classesCount: classesCount
                        }));
                        return;
                    }
                }
            }
        }

        // Validate MONTH frequency with WEEKS type
        if (
            pricingPack.frequency === "MONTH" &&
            pricingPack.frequencyType === "WEEKS" &&
            !pricingPack.all &&
            pricingPack.frequencyValue
        ) {
            const weeksByMonth: Record<string, Set<string>> = {};

            selectedWeeks.forEach((weekLabel: string) => {
                const [, weekTimestampStr] = weekLabel.split("+");
                const startOfWeek = timeutil.getInstance(Number(weekTimestampStr));

                for (let i = 0; i < 7; i++) {
                    const day = getInstanceClone(startOfWeek).add(i, "day");
                    const monthKey = day.format("YYYY-MM");

                    if (!weeksByMonth[monthKey]) {
                        weeksByMonth[monthKey] = new Set();
                    }

                    weeksByMonth[monthKey].add(weekLabel);
                }
            });
            let missingOrInvalid = false;
            selectedMonths.forEach((month) => {
                const monthKey = month.format("YYYY-MM");
                const weeks = weeksByMonth[monthKey];

                if (!weeks || weeks.size !== pricingPack.frequencyValue) {
                    notification(
                        "error",
                        formatMsg("bookSlots.selectWeeksForMonth", {
                            frequencyValue: pricingPack.frequencyValue,
                            month: month.format("MMMM YYYY")
                        })
                    );
                    missingOrInvalid = true;
                }
            });
            if (missingOrInvalid) {
                frequencyValueError = true;
            }
        }

        // Validate MONTH frequency with DAYS type
        if (
            pricingPack.frequency === "MONTH" &&
            pricingPack.frequencyType === "DAYS" &&
            !pricingPack.all &&
            pricingPack.frequencyValue
        ) {
            const daysByMonth = selectedDays.reduce((acc, day) => {
                const monthKey = timeutil.getEpoch(timeutil.getInstance(day).startOf("month")).toString();
                if (!acc[monthKey]) {
                    acc[monthKey] = [];
                }
                acc[monthKey].push(day);
                return acc;
            }, {} as Record<string, Date[]>);

            if (Object.entries(daysByMonth)?.length != selectedMonths.length) {
                notification("error", formatMsg("bookSlots.selectDaysForEachMonth", {
                    frequencyValue: pricingPack.frequencyValue
                }));
                frequencyValueError = true;
            } else {
                Object.entries(daysByMonth).forEach(([monthKey, days]) => {
                    if (days.length !== pricingPack.frequencyValue) {
                        notification(
                            "error",
                            formatMsg("bookSlots.daysNeedToBeSelectedForMonth", {
                                frequencyValue: pricingPack.frequencyValue,
                                month: timeutil.getInstance(
                                    parseInt(monthKey, 10)
                                ).format("MMMM YYYY")
                            })
                        );
                        frequencyValueError = true;
                    }
                });
            }
        }
        if (frequencyValueError) return;

        // Build slots from selectedTimings and selectedDays
        // Build slots: always send all slots (booked + new) to rescheduleBooking
        // Backend will remove previous slots and add all slots again, handling both modifications and additions
        const slots: any[] = [];
        const bookingId = typeof open === 'string' ? open : null;

        // Combine bookedSlots and newSelections - this gives us all slots (previous + new)
        // newSelections already excludes duplicates from bookedSlots, so this is safe
        const allSlots = [...bookedSlots, ...newSelections];

        // Convert all slots to the format expected by the API
        allSlots.forEach(slot => {
            slots.push({
                randomId: slot.slotRandomId || slot.randomId,
                date: typeof slot.date === 'number' ? slot.date : timeutil.getEpoch(timeutil.getInstance(slot.date)),
                startTime: slot.startTime,
                endTime: slot.endTime,
                activityId: slot.activityId ?? null,
            });
        });

        // Final validation: ensure we have slots to save (or allow empty if all were intentionally removed)
        // But for now, we require at least some slots to remain
        if (slots.length === 0 && !hasRemovals) {
            notification("error", formatMsg("error.selectslots"));
            return;
        }

        // Prepare bookedCredits function - count slots, not days
        const prepareBookedCredits = () => {
            if (!pricingPack) {
                return null;
            }

            // For CUSTOM with DURATION, we need durationValue and durationUnit
            if (pricingPack.frequency === "CUSTOM" && pricingPack.durationType === "DURATION") {
                if (!pricingPack.durationValue || !pricingPack.durationUnit) {
                    return null;
                }
            } else if (!pricingPack.frequencyValue || pricingPack.all) {
                // For other frequencies, we need frequencyValue and it shouldn't be "all"
                return null;
            }

            const newBookedCredits: Array<{ periodStartDate: number; periodEndDate: number; creditsUsed: number }> = [];

            // Helper function to count slots in a date range
            const countSlotsInRange = (startDate: number, endDate: number): number => {
                return slots.filter(slot => {
                    const slotDate = typeof slot.date === 'number' ? slot.date : timeutil.getEpoch(timeutil.getInstance(slot.date));
                    // Normalize slot date to start of day for comparison
                    const slotDateStart = timeutil.getEpoch(timeutil.getInstance(slotDate).startOf("day"));
                    return slotDateStart >= startDate && slotDateStart <= endDate;
                }).length;
            };

            // Get all unique dates from slots (not from selectedDays, as slots represent the final state)
            const getDatesFromSlots = (): Date[] => {
                const uniqueDates = new Set<number>();
                slots.forEach(slot => {
                    const slotDate = typeof slot.date === 'number' ? slot.date : timeutil.getEpoch(timeutil.getInstance(slot.date));
                    const slotDateStart = timeutil.getEpoch(timeutil.getInstance(slotDate).startOf("day"));
                    uniqueDates.add(slotDateStart);
                });
                return Array.from(uniqueDates).map(epoch => Helper.convertToDate(timeutil.getInstance(epoch), firebase));
            };

            const datesFromSlots = getDatesFromSlots();

            // Helper function to calculate windows from selected days for CUSTOM with DURATION
            const calculateWindowsFromDays = (days: Date[]) => {
                if (!pricingPack.durationValue || !pricingPack.durationUnit) {
                    return [];
                }

                const sortedDays = [...days].sort((a, b) => a.getTime() - b.getTime());
                const durationValue = pricingPack.durationValue;
                const durationUnit = pricingPack.durationUnit.toLowerCase();
                const windows: Array<{ start: Date; end: Date; days: Date[] }> = [];

                for (const day of sortedDays) {
                    const dayInstance = timeutil.getInstance(day);
                    let addedToWindow = false;

                    // Check if this day falls within any existing window
                    for (const window of windows) {
                        const windowStart = timeutil.getInstance(window.start);
                        const windowEnd = timeutil.getInstance(window.end);

                        if ((timeutil.isSame(dayInstance, windowStart, "day") || timeutil.isAfter(dayInstance, windowStart, "day")) &&
                            (timeutil.isSame(dayInstance, windowEnd, "day") || timeutil.isBefore(dayInstance, windowEnd, "day"))) {
                            window.days.push(day);
                            addedToWindow = true;
                            break;
                        }
                    }

                    // If not added to any existing window, create a new window
                    if (!addedToWindow) {
                        const windowStart = getInstanceClone(dayInstance).startOf("day");
                        let windowEnd = getInstanceClone(windowStart);

                        if (durationUnit === "months") {
                            windowEnd = windowEnd.add(durationValue, "month").endOf("day");
                        } else if (durationUnit === "weeks") {
                            windowEnd = windowEnd.add(durationValue, "week").endOf("day");
                        } else if (durationUnit === "days") {
                            windowEnd = windowEnd.add(durationValue - 1, "day").endOf("day");
                        }

                        windows.push({
                            start: Helper.convertToDate(windowStart, firebase),
                            end: Helper.convertToDate(windowEnd, firebase),
                            days: [day]
                        });
                    }
                }

                return windows;
            };

            switch (pricingPack.frequency) {
                case "CUSTOM": {
                    if (pricingPack.durationType === "DURATION") {
                        // Calculate windows from dates in slots (not selectedDays, as slots represent final state)
                        const windows = calculateWindowsFromDays(datesFromSlots);

                        // Create bookedCredits entry for each window
                        windows.forEach(window => {
                            const periodStartDate = timeutil.getEpoch(timeutil.getInstance(window.start).startOf("day"));
                            const periodEndDate = timeutil.getEpoch(timeutil.getInstance(window.end).endOf("day"));
                            // Count slots in this window, not days
                            const creditsUsed = countSlotsInRange(periodStartDate, periodEndDate);

                            if (creditsUsed > 0) {
                                newBookedCredits.push({
                                    periodStartDate,
                                    periodEndDate,
                                    creditsUsed
                                });
                            }
                        });
                    } else {
                        return null;
                    }
                    break;
                }
                case "WEEK": {
                    // Group dates from slots by week (not selectedDays, as slots represent final state)
                    const weeksMap = new Map<string, Date[]>();
                    datesFromSlots.forEach(day => {
                        const weekStart = timeutil.getStartOfWeek(day, selectedBatch?.weekStartDay);
                        const weekKey = timeutil.getEpoch(weekStart).toString();
                        if (!weeksMap.has(weekKey)) {
                            weeksMap.set(weekKey, []);
                        }
                        weeksMap.get(weekKey)!.push(day);
                    });

                    weeksMap.forEach((days, weekKey) => {
                        const weekStart = timeutil.getInstance(parseInt(weekKey, 10));
                        const periodStartDate = timeutil.getEpoch(weekStart);
                        const periodEndDate = timeutil.getEpoch(timeutil.getEndOfWeek(weekStart, selectedBatch?.weekStartDay));

                        // Count slots in this week, not days
                        const creditsUsed = countSlotsInRange(periodStartDate, periodEndDate);

                        if (creditsUsed > 0) {
                            newBookedCredits.push({
                                periodStartDate,
                                periodEndDate,
                                creditsUsed
                            });
                        }
                    });
                    break;
                }
                case "MONTH": {
                    if (pricingPack.frequencyType === "WEEKS") {
                        // Group selected weeks by month
                        const monthsMap = new Map<string, string[]>();
                        selectedWeeks.forEach(weekLabel => {
                            const [, weekTimestampStr] = weekLabel.split("+");
                            const weekStart = timeutil.getInstance(Number(weekTimestampStr));
                            const monthKey = weekStart.format("YYYY-MM");
                            if (!monthsMap.has(monthKey)) {
                                monthsMap.set(monthKey, []);
                            }
                            monthsMap.get(monthKey)!.push(weekLabel);
                        });

                        selectedMonths.forEach(month => {
                            const monthKey = month.format("YYYY-MM");
                            const weeks = monthsMap.get(monthKey) || [];
                            const periodStartDate = timeutil.getEpoch(month.clone().startOf("month"));
                            const periodEndDate = timeutil.getEpoch(month.clone().endOf("month"));

                            // Count slots in this month, not days
                            const creditsUsed = countSlotsInRange(periodStartDate, periodEndDate);

                            if (creditsUsed > 0) {
                                newBookedCredits.push({
                                    periodStartDate,
                                    periodEndDate,
                                    creditsUsed
                                });
                            }
                        });
                    } else if (pricingPack.frequencyType === "DAYS") {
                        // Group dates from slots by month (not selectedDays, as slots represent final state)
                        const monthsMap = new Map<string, Date[]>();
                        datesFromSlots.forEach(day => {
                            const monthStart = timeutil.getInstance(day).startOf("month");
                            const monthKey = timeutil.getEpoch(monthStart).toString();
                            if (!monthsMap.has(monthKey)) {
                                monthsMap.set(monthKey, []);
                            }
                            monthsMap.get(monthKey)!.push(day);
                        });

                        selectedMonths.forEach(month => {
                            const monthKey = timeutil.getEpoch(month.clone().startOf("month")).toString();
                            const days = monthsMap.get(monthKey) || [];
                            const periodStartDate = timeutil.getEpoch(month.clone().startOf("month"));
                            const periodEndDate = timeutil.getEpoch(month.clone().endOf("month"));
                            // Count slots in this month, not days
                            const creditsUsed = countSlotsInRange(periodStartDate, periodEndDate);

                            if (creditsUsed > 0) {
                                newBookedCredits.push({
                                    periodStartDate,
                                    periodEndDate,
                                    creditsUsed
                                });
                            }
                        });
                    }
                    break;
                }
                case "DAILY": {
                    // Each day is its own period - count slots per day
                    // Use dates from slots (not selectedDays) to represent final state
                    datesFromSlots.forEach(day => {
                        const dayInstance = timeutil.getInstance(day);
                        const periodStartDate = timeutil.getEpoch(dayInstance.startOf("day"));
                        const periodEndDate = timeutil.getEpoch(dayInstance.endOf("day"));
                        // Count slots on this day, not just 1
                        const creditsUsed = countSlotsInRange(periodStartDate, periodEndDate);

                        if (creditsUsed > 0) {
                            newBookedCredits.push({
                                periodStartDate,
                                periodEndDate,
                                creditsUsed
                            });
                        }
                    });
                    break;
                }
                default:
                    return null;
            }

            return newBookedCredits.length > 0 ? newBookedCredits : null;
        };
        // Calculate startDate and endDate
        // Window dates come ONLY from DB (editableWindowStart/End, rescheduleData, or bookingPeriod)
        // NOT calculated from selected slots
        let startDate: number | undefined;
        let endDate: number | undefined;

        // Priority 1: Use editable dates if set (user has edited or they were initialized from DB)
        if (editableWindowStart && editableWindowEnd) {
            startDate = timeutil.getEpoch(timeutil.getInstance(editableWindowStart).startOf("day"));
            endDate = timeutil.getEpoch(timeutil.getInstance(editableWindowEnd).endOf("day"));
        }
        // Priority 2: Check rescheduleData (from API - saved booking dates)
        else if (packageReducer.rescheduleData?.startDate && packageReducer.rescheduleData?.endDate) {
            startDate = timeutil.getEpoch(timeutil.getInstance(packageReducer.rescheduleData.startDate).startOf("day"));
            endDate = timeutil.getEpoch(timeutil.getInstance(packageReducer.rescheduleData.endDate).endOf("day"));
        }
        // Priority 3: Check bookingPeriod prop (saved booking dates from database)
        else if (bookingPeriod?.startDate && bookingPeriod?.endDate) {
            startDate = timeutil.getEpoch(timeutil.getInstance(bookingPeriod.startDate).startOf("day"));
            endDate = timeutil.getEpoch(timeutil.getInstance(bookingPeriod.endDate).endOf("day"));
        }
        // Priority 4: Fallback to batch dates
        else if (selectedBatch?.startDate && selectedBatch?.endDate) {
            startDate = timeutil.getEpoch(timeutil.getInstance(selectedBatch.startDate).startOf("day"));
            endDate = timeutil.getEpoch(timeutil.getInstance(selectedBatch.endDate).endOf("day"));
        }

        // Send all slots (previous + new) to rescheduleBooking
        // Backend will remove previous slots and add all slots again, handling both modifications and additions
        const payload = {
            "slots": slots.length ? slots : null,
            ...(bookingId && { "id": bookingId }),
            "startDate": startDate,
            "endDate": endDate,
            bookedCredits: prepareBookedCredits(),
            "activities": Helper.getUniqueActivityIds(selectedTimings)
        };

        // Always use rescheduleBooking - it handles both new bookings and rescheduling
        // by removing previous slots and adding all slots again
        dispatch(rescheduleBooking({ firebase, payload }));
    };

    // With no total to fill up to, the bar stays empty and the count reads "8", as for unlimited.
    const bookedPercentage = !noTotalCap && totalClasses > 0 ? (totalBooked / totalClasses) * 100 : 0;
    const selectedPercentage = !noTotalCap && totalClasses > 0 ? (totalSelected / totalClasses) * 100 : 0;

    // Find the very next upcoming slot (earliest future slot)
    const nextUpcomingSlot = useMemo(() => {
        const now = timeutil.getInstance();
        const allSlots = [...bookedSlots, ...newSelections];

        // Filter to only future slots and find the earliest one
        const futureSlots = allSlots.filter(slot => {
            const slotDate = typeof slot.date === 'number' ? slot.date : timeutil.getEpoch(timeutil.getInstance(slot.date));
            const slotDateTime = timeutil.getInstance(slotDate).set({
                hour: timeutil.getInstance(slot.startTime).hour(),
                minute: timeutil.getInstance(slot.startTime).minute(),
                second: 0,
                millisecond: 0
            });
            return timeutil.getEpoch(slotDateTime) > timeutil.getEpoch(now);
        });

        if (futureSlots.length === 0) return null;

        // Sort by date + startTime and get the earliest
        const sorted = futureSlots.sort((a, b) => {
            const dateA = typeof a.date === 'number' ? a.date : timeutil.getEpoch(timeutil.getInstance(a.date));
            const dateB = typeof b.date === 'number' ? b.date : timeutil.getEpoch(timeutil.getInstance(b.date));
            const dateTimeA = timeutil.getInstance(dateA).set({
                hour: timeutil.getInstance(a.startTime).hour(),
                minute: timeutil.getInstance(a.startTime).minute(),
                second: 0,
                millisecond: 0
            });
            const dateTimeB = timeutil.getInstance(dateB).set({
                hour: timeutil.getInstance(b.startTime).hour(),
                minute: timeutil.getInstance(b.startTime).minute(),
                second: 0,
                millisecond: 0
            });
            return timeutil.getEpoch(dateTimeA) - timeutil.getEpoch(dateTimeB);
        });

        return sorted[0];
    }, [bookedSlots, newSelections]);

    // Helper function to check if a slot is the next upcoming slot
    const isNextUpcomingSlot = (slot: BookedSlot): boolean => {
        if (!nextUpcomingSlot) return false;
        const slotDate = typeof slot.date === 'number' ? slot.date : timeutil.getEpoch(timeutil.getInstance(slot.date));
        const nextDate = typeof nextUpcomingSlot.date === 'number' ? nextUpcomingSlot.date : timeutil.getEpoch(timeutil.getInstance(nextUpcomingSlot.date));
        const slotId = slot.slotRandomId || slot.randomId || slot.id;
        const nextId = nextUpcomingSlot.slotRandomId || nextUpcomingSlot.randomId || nextUpcomingSlot.id;

        // Compare by date epoch, startTime, and slot ID
        const slotDateEpoch = timeutil.getEpoch(timeutil.getInstance(slotDate).startOf("day"));
        const nextDateEpoch = timeutil.getEpoch(timeutil.getInstance(nextDate).startOf("day"));

        return slotDateEpoch === nextDateEpoch &&
            slot.startTime === nextUpcomingSlot.startTime &&
            slot.endTime === nextUpcomingSlot.endTime &&
            slotId === nextId;
    };

    // open can be boolean or bookingId string (like RescheduleBookingModal)
    const isOpen = typeof open === 'boolean' ? open : !!open;
    if (!isOpen) return null;

    const modalFooter = (
        <div className="book-slots-footer">
            <div className="book-slots-footer-info">
                {newSelections.length > 0 ? (
                    <p className="book-slots-footer-text">
                        <span className="book-slots-footer-highlight">{newSelections.length}</span>{" "}
                        {newSelections.length === 1 ? (formatMsg("newSlotSelected")) : (formatMsg("newSlotsSelected"))}
                    </p>
                ) : (
                    <p className="book-slots-footer-text muted">{formatMsg("noNewSelections")}</p>
                )}
            </div>
            <div className="book-slots-footer-actions">
                <Button
                    onClick={() => setOpen(false)}
                    className="book-slots-cancel-btn"
                >
                    {formatMsg("cancel")}
                </Button>
                <Button
                    onClick={handleSave}
                    disabled={newSelections.length === 0 && bookedSlots.length === initialBookedClasses.length}
                    type="primary"
                    className="book-slots-save-btn"
                    loading={accountPackageReducer.isLoading}
                >
                    {formatMsg("saveBookings")}
                </Button>
            </div>
        </div>
    );

    return (
        <ModalWrapper
            title={formatMsg("bookSlots")}
            open={isOpen}
            onCancel={() => setOpen(false)}
            width={1200}
            footer={modalFooter}
            className="book-slots-modal"
        >
            <div className="book-slots-modal-content">
                {/* Header with booking period */}
                {(bookingPeriod || (packageReducer.rescheduleData?.startDate && packageReducer.rescheduleData?.endDate) ||
                    (editableWindowStart && editableWindowEnd) || (selectedBatch?.startDate && selectedBatch?.endDate)) && (
                        <div className="book-slots-header-info" style={{ display: 'flex', alignItems: 'center', gap: '10px', flexWrap: 'wrap' }}>
                            {!isEditingWindow ? (
                                <>
                                    <span>
                                        {formatMsg("membershipPeriod")} : {
                                            // Priority: editable dates > rescheduleData from API > bookingPeriod from DB > calculated
                                            (() => {
                                                if (editableWindowStart && editableWindowEnd) {
                                                    return `${timeutil.getInstance(editableWindowStart).format("MMM DD, YYYY")} - ${timeutil.getInstance(editableWindowEnd).format("MMM DD, YYYY")}`;
                                                } else if (packageReducer.rescheduleData?.startDate && packageReducer.rescheduleData?.endDate) {
                                                    return `${timeutil.getInstance(packageReducer.rescheduleData.startDate).format("MMM DD, YYYY")} - ${timeutil.getInstance(packageReducer.rescheduleData.endDate).format("MMM DD, YYYY")}`;
                                                } else if (bookingPeriod?.startDate && bookingPeriod?.endDate) {
                                                    return `${timeutil.getInstance(bookingPeriod.startDate).format("MMM DD, YYYY")} - ${timeutil.getInstance(bookingPeriod.endDate).format("MMM DD, YYYY")}`;
                                                } else {
                                                    return formatBookingPeriod();
                                                }
                                            })()
                                        }
                                    </span>
                                    {!readOnly && selectedPricingPack?.pricingPack?.frequency === "CUSTOM" &&
                                        selectedPricingPack?.pricingPack?.durationType === "DURATION" && (
                                            <Button
                                                type="text"
                                                size="small"
                                                icon={<EditOutlined />}
                                                onClick={() => {
                                                    // Prioritize saved dates from database over calculated dates
                                                    if (editableWindowStart && editableWindowEnd) {
                                                        // Already have editable dates set (from saved dates or previous calculation)
                                                        setIsEditingWindow(true);
                                                    } else if (packageReducer.rescheduleData?.startDate && packageReducer.rescheduleData?.endDate) {
                                                        // Use saved dates from API
                                                        const startDate = Helper.convertToDate(timeutil.getInstance(packageReducer.rescheduleData.startDate), firebase);
                                                        const endDate = Helper.convertToDate(timeutil.getInstance(packageReducer.rescheduleData.endDate), firebase);
                                                        setEditableWindowStart(startDate);
                                                        setEditableWindowEnd(endDate);
                                                        setIsEditingWindow(true);
                                                    } else if (bookingPeriod?.startDate && bookingPeriod?.endDate) {
                                                        // Use saved dates from bookingPeriod prop (from database)
                                                        const startDate = Helper.convertToDate(timeutil.getInstance(bookingPeriod.startDate), firebase);
                                                        const endDate = Helper.convertToDate(timeutil.getInstance(bookingPeriod.endDate), firebase);
                                                        setEditableWindowStart(startDate);
                                                        setEditableWindowEnd(endDate);
                                                        setIsEditingWindow(true);
                                                    } else {
                                                        // Fallback: Calculate from selected dates only if no saved dates exist
                                                        const windowDates = getCurrentWindowDates();
                                                        if (windowDates.start && windowDates.end) {
                                                            setEditableWindowStart(windowDates.start);
                                                            setEditableWindowEnd(windowDates.end);
                                                        }
                                                        setIsEditingWindow(true);
                                                    }
                                                }}
                                                style={{ padding: '0 4px' }}
                                            />
                                        )}
                                </>
                            ) : (
                                <>
                                    <span>{formatMsg("membershipPeriod")} :</span>
                                    <DatePicker
                                        value={editableWindowStart ? timeutil.getInstance(editableWindowStart) : null}
                                        onChange={(date) => {
                                            if (date) {
                                                const newStartDate = Helper.convertToDate(timeutil.getInstance(date), firebase);
                                                // Use current editableWindowEnd if available, otherwise try to get from saved dates
                                                let endDate = editableWindowEnd;
                                                if (!endDate) {
                                                    // Try to get from saved dates
                                                    if (packageReducer.rescheduleData?.endDate) {
                                                        endDate = Helper.convertToDate(timeutil.getInstance(packageReducer.rescheduleData.endDate), firebase);
                                                    } else if (bookingPeriod?.endDate) {
                                                        endDate = Helper.convertToDate(timeutil.getInstance(bookingPeriod.endDate), firebase);
                                                    } else {
                                                        // Last resort: calculate from current window
                                                        const windowDates = getCurrentWindowDates();
                                                        endDate = windowDates.end;
                                                    }
                                                }
                                                if (endDate) {
                                                    handleWindowDateChange(newStartDate, endDate);
                                                }
                                            }
                                        }}
                                        format={Helper.dateFormat(firebase)}
                                        size="small"
                                        style={{ width: '150px' }}
                                    />
                                    <span>-</span>
                                    <DatePicker
                                        value={editableWindowEnd ? timeutil.getInstance(editableWindowEnd) : null}
                                        onChange={(date) => {
                                            if (date) {
                                                const newEndDate = Helper.convertToDate(timeutil.getInstance(date), firebase);
                                                // Use current editableWindowStart if available, otherwise try to get from saved dates
                                                let startDate = editableWindowStart;
                                                if (!startDate) {
                                                    // Try to get from saved dates
                                                    if (packageReducer.rescheduleData?.startDate) {
                                                        startDate = Helper.convertToDate(timeutil.getInstance(packageReducer.rescheduleData.startDate), firebase);
                                                    } else if (bookingPeriod?.startDate) {
                                                        startDate = Helper.convertToDate(timeutil.getInstance(bookingPeriod.startDate), firebase);
                                                    } else {
                                                        // Last resort: calculate from current window
                                                        const windowDates = getCurrentWindowDates();
                                                        startDate = windowDates.start;
                                                    }
                                                }
                                                if (startDate) {
                                                    handleWindowDateChange(startDate, newEndDate);
                                                }
                                            }
                                        }}
                                        format={Helper.dateFormat(firebase)}
                                        size="small"
                                        style={{ width: '150px' }}
                                        disabledDate={(current) => {
                                            if (!editableWindowStart) return false;
                                            const startEpoch = timeutil.getEpoch(timeutil.getInstance(editableWindowStart).startOf("day"));
                                            return current && timeutil.getEpoch(current.startOf("day")) < startEpoch;
                                        }}
                                    />
                                    <Button
                                        type="text"
                                        size="small"
                                        icon={<CheckOutlined />}
                                        onClick={() => {
                                            setIsEditingWindow(false);
                                            // Mark that user has manually edited, so we don't auto-reset
                                            if (editableWindowStart && editableWindowEnd) {
                                                setHasManuallyEditedWindow(true);
                                                // Ensure the window dates are properly set
                                                handleWindowDateChange(editableWindowStart, editableWindowEnd);
                                            }
                                        }}
                                        style={{ padding: '0 4px' }}
                                    />
                                </>
                            )}
                        </div>
                    )}

                {/* Progress Bar */}
                <div className="book-slots-progress-container">
                    <div className="book-slots-progress-header">
                        <span className="book-slots-progress-label">{formatMsg("classesProgress")}</span>
                        <span className="book-slots-progress-count">
                            {noTotalCap
                                ? `${totalBooked + totalSelected}`
                                : `${totalBooked + totalSelected} / ${totalClasses}`}
                        </span>
                    </div>
                    <div className="book-slots-progress-bar">
                        <div
                            className="book-slots-progress-filled booked"
                            style={{ width: `${bookedPercentage}%` }}
                        />
                        <div
                            className="book-slots-progress-filled selecting"
                            style={{ width: `${selectedPercentage}%` }}
                        />
                    </div>
                    <div className="book-slots-progress-legend">
                        <div className="book-slots-legend-item">
                            <div className="book-slots-legend-dot booked"></div>
                            <span>{formatMsg("booked")} ({totalBooked})</span>
                        </div>
                        <div className="book-slots-legend-item">
                            <div className="book-slots-legend-dot selecting"></div>
                            <span>{formatMsg("selecting")} ({totalSelected})</span>
                        </div>
                        <div className="book-slots-legend-item">
                            <div className="book-slots-legend-dot available"></div>
                            <span>
                                {formatMsg("available")}
                                {remainingSlots !== undefined ? ` (${remainingSlots})` : ''}
                            </span>
                        </div>
                    </div>
                </div>

                {/* Main Content */}
                <Row gutter={24} className="book-slots-main-content">
                    {/* Left Column: New Selections and Previously Booked Classes */}
                    <Col xs={24} lg={10}>
                        {/* New Selections Section */}
                        {totalSelected > 0 && (
                            <div className="book-slots-section" style={{ marginBottom: 12 }}>
                                <div className="book-slots-section-header">
                                    <CalendarOutlined className="book-slots-section-icon selecting-icon" />
                                    <h3 className="book-slots-section-title">
                                        {formatMsg("newSelections")} ({totalSelected})
                                    </h3>
                                </div>
                                <div className="book-slots-booked-list">
                                    {[...newSelections]
                                        .sort((a, b) => {
                                            const dateA = timeutil.getEpoch(timeutil.getInstance(a.date));
                                            const dateB = timeutil.getEpoch(timeutil.getInstance(b.date));
                                            return dateB - dateA; // Descending order (newest first)
                                        })
                                        .map((slot) => {
                                            const uniqueKey = `${timeutil.getEpoch(timeutil.getInstance(slot.date).startOf("day"))}-${slot.slotRandomId || slot.randomId || slot.id}-${slot.startTime}-${slot.endTime}`;
                                            const dateInstance = timeutil.getInstance(slot.date);
                                            const dayName = dateInstance.format("dddd");
                                            const isUpcoming = uniqueKey === nextUpcomingSlotKey;
                                            return (
                                                <div
                                                    key={uniqueKey}
                                                    className="book-slots-slot-item-flat selecting-slot-item"
                                                >
                                                    <div className="book-slots-slot-date-pill">
                                                        <span className="book-slots-slot-date-day">{dateInstance.format("DD")}</span>
                                                        <span className="book-slots-slot-date-month">{dateInstance.format("MMM")}</span>
                                                    </div>
                                                    <div className="book-slots-slot-content-flat">
                                                        <p className="book-slots-slot-time-flat">
                                                            {formatTime(slot.startTime)} - {formatTime(slot.endTime)}
                                                        </p>
                                                        <div className="book-slots-slot-day-activity-wrapper">
                                                            <span className="book-slots-slot-day-tag">{dayName}</span>
                                                            {isUpcoming && (
                                                                <span className="book-slots-slot-upcoming-tag">
                                                                    {formatMsg("upcoming")}
                                                                </span>
                                                            )}
                                                            {slot.activityId && slot.activityId !== "default" && firebase.schoolConfig.showPackageActivities && packageActivityMap.get(slot.activityId)?.name && (
                                                                <span className="book-slots-slot-activity-name">{packageActivityMap.get(slot.activityId).name}</span>
                                                            )}
                                                        </div>
                                                    </div>
                                                </div>
                                            );
                                        })}
                                </div>
                            </div>
                        )}

                        {/* Previously Booked Section */}
                        <div className="book-slots-section">
                            <div className="book-slots-section-header">
                                <CheckOutlined className="book-slots-section-icon booked-icon" />
                                <h3 className="book-slots-section-title">
                                    {formatMsg("previouslyBooked")} ({totalBooked})
                                </h3>
                            </div>

                            {totalBooked === 0 ? (
                                <div className="book-slots-empty-state">
                                    <CalendarOutlined className="book-slots-empty-icon" />
                                    <p className="book-slots-empty-text">{formatMsg("noClassesBookedYet")}</p>
                                    <p className="book-slots-empty-subtext">{formatMsg("startBySelectingDatesAndTimes")}</p>
                                </div>
                            ) : (
                                <div className="book-slots-booked-list">
                                    {[...bookedSlots]
                                        .sort((a, b) => {
                                            const dateA = timeutil.getEpoch(timeutil.getInstance(a.date));
                                            const dateB = timeutil.getEpoch(timeutil.getInstance(b.date));
                                            return dateB - dateA; // Descending order (newest first)
                                        })
                                        .map(slot => {
                                            // Use the same unique key as in removeBookedSlot for consistency
                                            const slotId = slot.slotRandomId || slot.randomId || slot.id;
                                            const dateEpoch = timeutil.getEpoch(timeutil.getInstance(slot.date).startOf("day"));
                                            const uniqueKey = `${dateEpoch}-${slotId}-${slot.startTime}-${slot.endTime}`;
                                            const dateInstance = timeutil.getInstance(slot.date);
                                            const dayName = dateInstance.format("dddd");
                                            const isUpcoming = uniqueKey === nextUpcomingSlotKey;

                                            // Check permissions for delete button
                                            // Try to get centerId from slot first, then fallback to rescheduleData
                                            const centerId = (slot as any)?.centerId || packageReducer.rescheduleData?.centerId;
                                            const hasReschedulePermission = centerId
                                                ? permissions.hasPermissionForEntity(firebase, permissions.PERMISSIONS.BOOKING_RESCHEDULE, [centerId])
                                                : false;
                                            const hasCancelPermission = centerId
                                                ? permissions.hasPermissionForEntity(firebase, permissions.PERMISSIONS.BOOKING_CANCEL, [centerId])
                                                : false;
                                            const canDelete = hasReschedulePermission || hasCancelPermission;

                                            return (
                                                <div
                                                    key={uniqueKey}
                                                    className="book-slots-slot-item-flat booked-slot-item"
                                                >
                                                    <div className="book-slots-slot-date-pill">
                                                        <span className="book-slots-slot-date-day">{dateInstance.format("DD")}</span>
                                                        <span className="book-slots-slot-date-month">{dateInstance.format("MMM")}</span>
                                                    </div>
                                                    <div className="book-slots-slot-content-flat">
                                                        <p className="book-slots-slot-time-flat">
                                                            {formatTime(slot.startTime)} - {formatTime(slot.endTime)}
                                                        </p>
                                                        <div className="book-slots-slot-day-activity-wrapper">
                                                            <span className="book-slots-slot-day-tag">{dayName}</span>
                                                            {isUpcoming && (
                                                                <span className="book-slots-slot-upcoming-tag">
                                                                    {formatMsg("upcoming")}
                                                                </span>
                                                            )}
                                                            {slot.activityId && slot.activityId !== "default" && firebase.schoolConfig.showPackageActivities && packageActivityMap.get(slot.activityId)?.name && (
                                                                <span className="book-slots-slot-activity-name">{packageActivityMap.get(slot.activityId).name}</span>
                                                            )}
                                                        </div>
                                                    </div>
                                                    {!readOnly && canDelete && (
                                                        <button
                                                            onClick={() => removeBookedSlot(slot)}
                                                            className="book-slots-remove-btn"
                                                            title={formatMsg("removeBooking")}
                                                        >
                                                            <DeleteOutlined />
                                                        </button>
                                                    )}
                                                </div>
                                            );
                                        })}
                                </div>
                            )}
                        </div>
                    </Col>

                    {/* Right Column: Available to Book */}
                    <Col xs={24} lg={14}>
                        <div className="book-slots-section">
                            <div className="book-slots-section-header">
                                <CalendarOutlined className="book-slots-section-icon available-icon" />
                                <h3 className="book-slots-section-title">
                                    {formatMsg("availableToBookDatesAndTimings")}
                                </h3>
                                {remainingSlots === 0 && totalClasses > 0 && !noTotalCap && (
                                    <div className="book-slots-limit-badge">
                                        <ExclamationCircleOutlined />
                                        <span>{formatMsg("limitReached")}</span>
                                    </div>
                                )}
                            </div>

                            {remainingSlots === 0 && totalSelected === 0 && totalClasses > 0 && !noTotalCap ? (
                                <div className="book-slots-warning-state">
                                    <ExclamationCircleOutlined className="book-slots-warning-icon" />
                                    <p className="book-slots-warning-text">{formatMsg("allClassesBooked")}</p>
                                    <p className="book-slots-warning-subtext">
                                        {formatMsg("removeExistingBookingsToSelectDifferentSlots")}
                                    </p>
                                </div>
                            ) : (
                                <div className="book-slots-available-content">
                                    {isLoading ? (
                                        <Skeleton active />
                                    ) : selectedBatch?.id && selectedPricingPack?.pricingPack && Object.keys(groupedTimings).length > 0 ? (
                                        <div className="book-slots-calendar-timings-wrapper">
                                            {/* Calendar Section - Just DayPicker */}
                                            <div className="book-slots-calendar-section">
                                                <div className="b-slots-container">
                                                    <DayPicker
                                                        key={`daypicker-${editableWindowStart ? timeutil.getEpoch(timeutil.getInstance(editableWindowStart).startOf("day")) : 'none'}-${editableWindowEnd ? timeutil.getEpoch(timeutil.getInstance(editableWindowEnd).startOf("day")) : 'none'}`}
                                                        timeZone={firebase.schoolConfig.timezone}
                                                        mode="multiple"
                                                        onSelect={(days) => {
                                                            if (readOnly) return;
                                                            if (!days) {
                                                                setSelectedDays([]);
                                                                // Clear all selectedTimings when all dates are deselected
                                                                setSelectedTimings({});
                                                                return;
                                                            }
                                                            const sortedDays = Array.isArray(days) ? days.sort((a, b) => a.getTime() - b.getTime()) : [days];

                                                            // For CUSTOM with DURATION (both unlimited and limited classes), validate that new dates are within the window
                                                            const pricingPack = selectedPricingPack?.pricingPack;
                                                            if (pricingPack?.frequency === "CUSTOM" &&
                                                                pricingPack?.durationType === "DURATION" &&
                                                                pricingPack?.durationValue &&
                                                                pricingPack?.durationUnit) {

                                                                // Determine window dates: ONLY from DB (editableWindowStart/End, rescheduleData, or bookingPeriod)
                                                                // NOT calculated from selected dates
                                                                let windowStartDate: Date | null = null;
                                                                let windowEndDate: Date | null = null;

                                                                // Priority 1: Use saved window dates from database (editableWindowStart/End)
                                                                if (editableWindowStart && editableWindowEnd) {
                                                                    windowStartDate = editableWindowStart;
                                                                    windowEndDate = editableWindowEnd;
                                                                } else if (packageReducer.rescheduleData?.startDate && packageReducer.rescheduleData?.endDate) {
                                                                    // Priority 2: Use rescheduleData from API
                                                                    windowStartDate = Helper.convertToDate(timeutil.getInstance(packageReducer.rescheduleData.startDate), firebase);
                                                                    windowEndDate = Helper.convertToDate(timeutil.getInstance(packageReducer.rescheduleData.endDate), firebase);
                                                                } else if (bookingPeriod?.startDate && bookingPeriod?.endDate) {
                                                                    // Priority 3: Use bookingPeriod prop
                                                                    windowStartDate = Helper.convertToDate(timeutil.getInstance(bookingPeriod.startDate), firebase);
                                                                    windowEndDate = Helper.convertToDate(timeutil.getInstance(bookingPeriod.endDate), firebase);
                                                                } else if (selectedBatch?.startDate && selectedBatch?.endDate) {
                                                                    // Priority 4: Fallback to batch dates
                                                                    windowStartDate = Helper.convertToDate(timeutil.getInstance(selectedBatch.startDate), firebase);
                                                                    windowEndDate = Helper.convertToDate(timeutil.getInstance(selectedBatch.endDate), firebase);
                                                                }

                                                                // Only validate if we have window dates
                                                                if (windowStartDate && windowEndDate) {
                                                                    const windowStartEpoch = timeutil.getEpoch(timeutil.getInstance(windowStartDate).startOf("day"));
                                                                    const windowEndEpoch = timeutil.getEpoch(timeutil.getInstance(windowEndDate).endOf("day"));

                                                                    // Check if any new date is outside the window
                                                                    const datesOutsideWindow = sortedDays.filter(day => {
                                                                        const dayEpoch = timeutil.getEpoch(timeutil.getInstance(day).startOf("day"));
                                                                        return dayEpoch < windowStartEpoch || dayEpoch > windowEndEpoch;
                                                                    });

                                                                    if (datesOutsideWindow.length > 0) {
                                                                        const windowStartStr = timeutil.getInstance(windowStartEpoch).format("MMM DD, YYYY");
                                                                        const windowEndStr = timeutil.getInstance(windowEndEpoch).format("MMM DD, YYYY");
                                                                        const durationValue = pricingPack.durationValue;
                                                                        const durationUnit = pricingPack.durationUnit.toLowerCase();
                                                                        notification("error", formatMsg("bookSlots.selectedDatesMustBeWithinWindow", {
                                                                            durationValue: durationValue,
                                                                            durationUnit: durationUnit,
                                                                            windowStartStr: windowStartStr,
                                                                            windowEndStr: windowEndStr
                                                                        }));
                                                                        return;
                                                                    }
                                                                }
                                                            }

                                                            // Check if adding new dates would exceed limit (only if dates are being added, not removed)
                                                            // Skip this check if unlimitedClasses is true
                                                            const previousDaysCount = selectedDays.length;
                                                            const newDaysCount = sortedDays.length;
                                                            if (newDaysCount > previousDaysCount && !noTotalCap) {
                                                                // New dates are being added - check if we have capacity
                                                                // We can't know exactly how many slots will be added until timings are selected,
                                                                // but we can at least check if we're already at or over the limit
                                                                const currentTotal = totalBooked + newSelections.length;
                                                                if (currentTotal >= totalClasses) {
                                                                    notification("error", formatMsg("bookSlots.cannotSelectMoreDates", {
                                                                        totalClasses: totalClasses
                                                                    }));
                                                                    return;
                                                                }
                                                            }

                                                            // Find dates that were removed
                                                            const previousDays = selectedDays.map(d =>
                                                                timeutil.getEpoch(timeutil.getInstance(d).startOf("day"))
                                                            );
                                                            const newDays = sortedDays.map(d =>
                                                                timeutil.getEpoch(timeutil.getInstance(d).startOf("day"))
                                                            );
                                                            const removedDays = previousDays.filter(epoch => !newDays.includes(epoch));

                                                            // Clean up selectedTimings for removed dates
                                                            if (removedDays.length > 0) {
                                                                const updatedTimings = { ...selectedTimings };
                                                                const isRecurring = selectedBatch?.type === "recurring";

                                                                removedDays.forEach(removedEpoch => {
                                                                    const removedDate = timeutil.getInstance(removedEpoch);
                                                                    const dayOrDateKey = isRecurring
                                                                        ? removedDate.format("dddd").toUpperCase()
                                                                        : removedDate.format(Helper.dateFormat(firebase));

                                                                    // Remove timings for this date from all activities
                                                                    Object.keys(updatedTimings).forEach(actKey => {
                                                                        if (updatedTimings[actKey]?.[dayOrDateKey]) {
                                                                            delete updatedTimings[actKey][dayOrDateKey];
                                                                            // Clean up empty activity objects
                                                                            if (Object.keys(updatedTimings[actKey]).length === 0) {
                                                                                delete updatedTimings[actKey];
                                                                            }
                                                                        }
                                                                    });
                                                                });

                                                                setSelectedTimings(updatedTimings);
                                                            }

                                                            // Validate CUSTOM with DURATION
                                                            if (selectedPricingPack?.pricingPack?.frequency === "CUSTOM" &&
                                                                selectedPricingPack?.pricingPack?.durationType === "DURATION") {
                                                                const durationValue = selectedPricingPack.pricingPack.durationValue;
                                                                const durationUnit = selectedPricingPack.pricingPack.durationUnit?.toLowerCase();

                                                                if (durationValue && durationUnit) {
                                                                    // Calculate windows from selected days
                                                                    const windows: Array<{ start: Date; end: Date; days: Date[] }> = [];
                                                                    for (const day of sortedDays) {
                                                                        const dayInstance = timeutil.getInstance(day);
                                                                        let addedToWindow = false;

                                                                        for (const window of windows) {
                                                                            const windowStart = timeutil.getInstance(window.start);
                                                                            const windowEnd = timeutil.getInstance(window.end);

                                                                            if ((timeutil.isSame(dayInstance, windowStart, "day") || timeutil.isAfter(dayInstance, windowStart, "day")) &&
                                                                                (timeutil.isSame(dayInstance, windowEnd, "day") || timeutil.isBefore(dayInstance, windowEnd, "day"))) {
                                                                                window.days.push(day);
                                                                                addedToWindow = true;
                                                                                break;
                                                                            }
                                                                        }

                                                                        if (!addedToWindow) {
                                                                            const windowStart = getInstanceClone(dayInstance).startOf("day");
                                                                            let windowEnd = getInstanceClone(windowStart);

                                                                            if (durationUnit === "months") {
                                                                                windowEnd = windowEnd.add(durationValue, "month").endOf("day");
                                                                            } else if (durationUnit === "weeks") {
                                                                                windowEnd = windowEnd.add(durationValue, "week").endOf("day");
                                                                            } else if (durationUnit === "days") {
                                                                                windowEnd = windowEnd.add(durationValue - 1, "day").endOf("day");
                                                                            }

                                                                            windows.push({
                                                                                start: Helper.convertToDate(windowStart, firebase),
                                                                                end: Helper.convertToDate(windowEnd, firebase),
                                                                                days: [day]
                                                                            });
                                                                        }
                                                                    }

                                                                    // Check if any window exceeds noOfClasses. A booking under per-period
                                                                    // counting counts per day/week/month, billing period or whole pack;
                                                                    // older bookings keep the "Pack runs for" windows built above.
                                                                    const capWins = limitWindows(sortedDays, selectedPricingPack.pricingPack, {
                                                                        periodCounting: packageReducer.rescheduleData?.allowanceCounting === PERIOD_COUNTING,
                                                                        anchor: packageReducer.rescheduleData?.startDate ?? null,
                                                                        legacy: () => windows,
                                                                    });
                                                                    if (!selectedPricingPack.pricingPack.unlimitedClasses && selectedPricingPack.pricingPack.noOfClasses) {
                                                                        for (const window of capWins) {
                                                                            let classesCount = 0;
                                                                            window.days.forEach(day => {
                                                                                const isRecurring = selectedBatch.type === "recurring";
                                                                                const dayOrDateStr = isRecurring
                                                                                    ? timeutil.inFormat(day, "dddd").toLowerCase()
                                                                                    : timeutil.getInstance(day).format(Helper.dateFormat(firebase));

                                                                                Object.values(selectedTimings).forEach((activitySelection: any) => {
                                                                                    if (!activitySelection) return;
                                                                                    const commonSlots = activitySelection["Common slots"];
                                                                                    if (commonSlots) {
                                                                                        classesCount += Array.isArray(commonSlots) ? commonSlots.length : 1;
                                                                                    }
                                                                                    const timingsForDay = activitySelection[dayOrDateStr.toUpperCase()];
                                                                                    if (timingsForDay) {
                                                                                        classesCount += Array.isArray(timingsForDay) ? timingsForDay.length : 1;
                                                                                    }
                                                                                });
                                                                            });

                                                                            if (classesCount > selectedPricingPack.pricingPack.noOfClasses) {
                                                                                notification("error", formatMsg("bookSlots.windowExceedsClassesLimit", {
                                                                                    noOfClasses: selectedPricingPack.pricingPack.noOfClasses
                                                                                }));
                                                                                return;
                                                                            }
                                                                        }
                                                                    }
                                                                }
                                                            }

                                                            setSelectedDays(sortedDays);
                                                        }}
                                                        numberOfMonths={1}
                                                        pagedNavigation
                                                        showOutsideDays
                                                        selected={selectedDays}
                                                        month={Helper.convertToDate(currentMonth, firebase)}
                                                        captionLayout="dropdown"
                                                        onMonthChange={(month) => setCurrentMonth(timeutil.getInstance(month))}
                                                        modifiers={{
                                                            selected: selectedDays,
                                                            disabled: disabled,
                                                            booked: (date: Date) => {
                                                                const dateEpoch = timeutil.getEpoch(timeutil.getInstance(date).startOf("day"));
                                                                return getBookedDates().has(dateEpoch);
                                                            }
                                                        }}
                                                        weekStartsOn={timeutil.getWeekStartDow(selectedBatch?.weekStartDay)}
                                                        disabled={disabled}
                                                        startMonth={editableWindowStart || (selectedBatch?.startDate ? Helper.convertToDate(timeutil.getInstance(selectedBatch.startDate), firebase) : (selectedBatch?.intervals?.length ? Helper.convertToDate(timeutil.getInstance(Math.min(...selectedBatch.intervals.map((d: any) => timeutil.getEpoch(timeutil.getInstance(d.date))))), firebase) : new Date()))}
                                                        endMonth={editableWindowEnd || (selectedBatch?.endDate ? Helper.convertToDate(timeutil.getInstance(selectedBatch.endDate), firebase) : (selectedBatch?.intervals?.length ? Helper.convertToDate(timeutil.getInstance(Math.max(...selectedBatch.intervals.map((d: any) => timeutil.getEpoch(timeutil.getInstance(d.date))))), firebase) : new Date()))}
                                                        styles={{ day_button: { cursor: readOnly ? "text" : undefined } }}
                                                        animate
                                                    />
                                                </div>
                                            </div>

                                            {/* Timings Section */}
                                            <div className="book-slots-timings-section">
                                                <div className="book-slots-timings-container">
                                                    {selectedDays.length === 0 ? (
                                                        <div className="book-slots-timings-placeholder">
                                                            <ClockCircleOutlined className="book-slots-placeholder-icon" />
                                                            <p className="book-slots-placeholder-text">{formatMsg("selectDatesToViewTimings")}</p>
                                                        </div>
                                                    ) : (
                                                        Object.entries(groupedTimings).map(([activityId, slotsByDay]: any) => {
                                                            if (!slotsByDay) return null;
                                                            // Check if there are timings for new dates (including Common slots if dates are selected)
                                                            const hasCommonSlots = slotsByDay["Common slots"] && selectedDays.length > 0;
                                                            const hasTimingsForNewDates = Object.entries(slotsByDay).some(([dayOrDate, timings]: any) => {
                                                                if (dayOrDate === 'Common slots') return hasCommonSlots;
                                                                return getFilteredDays(dayOrDate);
                                                            });
                                                            if (!hasTimingsForNewDates) return null;
                                                            return (
                                                                <div key={activityId} className="book-slots-activity-group act-timing-box">
                                                                    {activityId !== 'default' && firebase.schoolConfig.showPackageActivities && (
                                                                        <div className="book-slots-activity-header">
                                                                            {packageActivityMap.get(activityId)?.name || ""}
                                                                        </div>
                                                                    )}

                                                                    {/* Render Common slots first if dates are selected */}
                                                                    {slotsByDay["Common slots"] && selectedDays.length > 0 && (() => {
                                                                        const commonSlots = Array.isArray(slotsByDay["Common slots"])
                                                                            ? slotsByDay["Common slots"]
                                                                            : [slotsByDay["Common slots"]];

                                                                        return (
                                                                            <div className="book-slots-day-timings">
                                                                                <div className="book-slots-day-header-small">
                                                                                    {formatMsg("commonSlots")}
                                                                                </div>
                                                                                <div className="book-slots-timings-grid">
                                                                                    {commonSlots.map((timing: any) => {
                                                                                        const isSelected = selectedTimings[activityId]?.["Common slots"]?.includes(timing.key);
                                                                                        const availability = getAvailabilityPerSlot(timing, activityId, "Common slots");
                                                                                        return (
                                                                                            <button
                                                                                                key={timing.key}
                                                                                                onClick={() => {
                                                                                                    if (readOnly) return;
                                                                                                    const actKey = activityId ?? "default";
                                                                                                    const updatedTimings = { ...selectedTimings };
                                                                                                    if (!updatedTimings[actKey]) {
                                                                                                        updatedTimings[actKey] = {};
                                                                                                    }
                                                                                                    const currentSelection = updatedTimings[actKey]["Common slots"];
                                                                                                    const isMultiple = !!selectedBatch?.allowMultipleTimeSlots;

                                                                                                    if (Array.isArray(currentSelection)) {
                                                                                                        if (currentSelection.includes(timing.key)) {
                                                                                                            // Removing a timing - always allowed
                                                                                                            updatedTimings[actKey]["Common slots"] = currentSelection.filter(
                                                                                                                (id: string) => id !== timing.key
                                                                                                            );
                                                                                                        } else {
                                                                                                            // Adding a new timing - check if multiple slots are allowed
                                                                                                            if (!isMultiple) {
                                                                                                                // Check if there are any booked slots for the affected dates
                                                                                                                const affectedDates = getAffectedDates("Common slots");
                                                                                                                const bookedCheck = hasBookedSlotsForDates(affectedDates);
                                                                                                                if (bookedCheck.hasBooked) {
                                                                                                                    const isRecurring = selectedBatch?.type === "recurring";
                                                                                                                    const bookedDateStr = bookedCheck.bookedDate
                                                                                                                        ? (isRecurring
                                                                                                                            ? timeutil.getInstance(bookedCheck.bookedDate).format("dddd")
                                                                                                                            : timeutil.getInstance(bookedCheck.bookedDate).format("MMM DD, YYYY"))
                                                                                                                        : "";
                                                                                                                    notification("error", formatMsg("bookSlots.removeExistingBookingFirst", {
                                                                                                                        date: bookedDateStr
                                                                                                                    }) || `Please remove the existing booking for ${bookedDateStr} before selecting a different slot.`);
                                                                                                                    return;
                                                                                                                }

                                                                                                                // Remove "Common slots" from ALL activities
                                                                                                                Object.keys(updatedTimings).forEach(actKeyToRemove => {
                                                                                                                    const activitySelection = updatedTimings[actKeyToRemove];
                                                                                                                    if (!activitySelection) return;

                                                                                                                    // Remove Common slots from this activity
                                                                                                                    if (activitySelection["Common slots"]) {
                                                                                                                        delete activitySelection["Common slots"];
                                                                                                                    }
                                                                                                                });

                                                                                                                // Remove day-specific slots for all affected dates from ALL activities
                                                                                                                const isRecurring = selectedBatch?.type === "recurring";
                                                                                                                affectedDates.forEach(dateEpoch => {
                                                                                                                    let keyToCheck: string;
                                                                                                                    if (isRecurring) {
                                                                                                                        const dateInstance = timeutil.getInstance(dateEpoch);
                                                                                                                        keyToCheck = dateInstance.format("dddd").toUpperCase();
                                                                                                                    } else {
                                                                                                                        const dateInstance = timeutil.getInstance(dateEpoch);
                                                                                                                        keyToCheck = dateInstance.format(Helper.dateFormat(firebase));
                                                                                                                    }

                                                                                                                    // Remove this day from ALL activities
                                                                                                                    Object.keys(updatedTimings).forEach(actKeyToRemove => {
                                                                                                                        const activitySelection = updatedTimings[actKeyToRemove];
                                                                                                                        if (!activitySelection) return;

                                                                                                                        if (activitySelection[keyToCheck]) {
                                                                                                                            delete activitySelection[keyToCheck];
                                                                                                                        }
                                                                                                                    });
                                                                                                                });

                                                                                                                // Clean up empty activity objects
                                                                                                                Object.keys(updatedTimings).forEach(actKey => {
                                                                                                                    if (Object.keys(updatedTimings[actKey]).length === 0) {
                                                                                                                        delete updatedTimings[actKey];
                                                                                                                    }
                                                                                                                });
                                                                                                            }

                                                                                                            // Check if it would exceed limit
                                                                                                            // Skip limit check if unlimitedClasses is true
                                                                                                            if (!noTotalCap) {
                                                                                                                const estimatedNewSlots = estimateNewSlotsForTiming("Common slots", timing.key, activityId);

                                                                                                                // Check if adding this timing would exceed the limit
                                                                                                                const currentTotal = totalBooked + newSelections.length;
                                                                                                                if (currentTotal + estimatedNewSlots > totalClasses) {
                                                                                                                    notification("error", formatMsg("bookSlots.cannotSelectMoreSlotsExceedLimit", {
                                                                                                                        totalBooked: totalBooked,
                                                                                                                        newSelections: newSelections.length,
                                                                                                                        totalClasses: totalClasses
                                                                                                                    }));
                                                                                                                    return;
                                                                                                                }

                                                                                                                if (remainingSlots <= 0 && currentSelection.length === 0) {
                                                                                                                    notification("error", formatMsg("bookSlots.cannotSelectMoreSlotsLimitReached", {
                                                                                                                        totalClasses: totalClasses
                                                                                                                    }));
                                                                                                                    return;
                                                                                                                }
                                                                                                            }

                                                                                                            // Allow selection if unlimitedClasses is true or if within limit
                                                                                                            // Ensure actKey exists (might have been deleted during cleanup)
                                                                                                            if (!updatedTimings[actKey]) {
                                                                                                                updatedTimings[actKey] = {};
                                                                                                            }
                                                                                                            // If multiple slots are allowed, append to existing array; otherwise replace
                                                                                                            if (isMultiple) {
                                                                                                                updatedTimings[actKey]["Common slots"] = [...currentSelection, timing.key];
                                                                                                            } else {
                                                                                                                // After removing from all activities, currentSelection might be undefined
                                                                                                                // So we start fresh with just the new timing
                                                                                                                updatedTimings[actKey]["Common slots"] = [timing.key];
                                                                                                            }
                                                                                                        }
                                                                                                    } else {
                                                                                                        // Adding first timing for Common slots - check if multiple slots are allowed
                                                                                                        if (!isMultiple) {
                                                                                                            // Check if there are any booked slots for the affected dates
                                                                                                            const affectedDates = getAffectedDates("Common slots");
                                                                                                            const bookedCheck = hasBookedSlotsForDates(affectedDates);
                                                                                                            if (bookedCheck.hasBooked) {
                                                                                                                const isRecurring = selectedBatch?.type === "recurring";
                                                                                                                const bookedDateStr = bookedCheck.bookedDate
                                                                                                                    ? (isRecurring
                                                                                                                        ? timeutil.getInstance(bookedCheck.bookedDate).format("dddd")
                                                                                                                        : timeutil.getInstance(bookedCheck.bookedDate).format("MMM DD, YYYY"))
                                                                                                                    : "";
                                                                                                                notification("error", formatMsg("bookSlots.removeExistingBookingFirst", {
                                                                                                                    date: bookedDateStr
                                                                                                                }) || `Please remove the existing booking for ${bookedDateStr} before selecting a different slot.`);
                                                                                                                return;
                                                                                                            }
                                                                                                        }

                                                                                                        // Check limit
                                                                                                        // Skip limit check if unlimitedClasses is true
                                                                                                        if (!noTotalCap) {
                                                                                                            const estimatedNewSlots = estimateNewSlotsForTiming("Common slots", timing.key, activityId);

                                                                                                            // Check if adding this timing would exceed the limit
                                                                                                            const currentTotal = totalBooked + newSelections.length;
                                                                                                            if (currentTotal + estimatedNewSlots > totalClasses) {
                                                                                                                notification("error", formatMsg("bookSlots.cannotSelectMoreSlotsExceedLimit", {
                                                                                                                    totalBooked: totalBooked,
                                                                                                                    newSelections: newSelections.length,
                                                                                                                    totalClasses: totalClasses
                                                                                                                }));
                                                                                                                return;
                                                                                                            }

                                                                                                            if (remainingSlots <= 0) {
                                                                                                                notification("error", formatMsg("bookSlots.cannotSelectMoreSlotsLimitReached", {
                                                                                                                    totalClasses: totalClasses
                                                                                                                }));
                                                                                                                return;
                                                                                                            }
                                                                                                        }

                                                                                                        // Allow selection if unlimitedClasses is true or if within limit
                                                                                                        // Ensure actKey exists (might have been deleted during cleanup)
                                                                                                        if (!updatedTimings[actKey]) {
                                                                                                            updatedTimings[actKey] = {};
                                                                                                        }
                                                                                                        updatedTimings[actKey]["Common slots"] = [timing.key];
                                                                                                    }
                                                                                                    setSelectedTimings(updatedTimings);
                                                                                                }}
                                                                                                className={`book-slots-timing-button ${isSelected ? 'selected' : ''}`}
                                                                                                disabled={readOnly || !selectedDays.length || availability.text === "Sold out"}
                                                                                            >
                                                                                                <span style={{ flex: 1 }}>{timing.label}</span>
                                                                                                {availability.text && (
                                                                                                    <span
                                                                                                        className="book-slots-availability-badge"
                                                                                                        style={{
                                                                                                            color: getAvailabilityFontColor(availability),
                                                                                                            fontSize: '11px',
                                                                                                            fontWeight: 600,
                                                                                                            whiteSpace: 'nowrap',
                                                                                                            backgroundColor: isSelected
                                                                                                                ? (availability.color === '#8D8D8D' ? '#8D8D8D' : availability.color)
                                                                                                                : (availability.color || '#8D8D8D'),
                                                                                                            padding: '4px 10px',
                                                                                                            borderRadius: '12px',
                                                                                                            display: 'inline-flex',
                                                                                                            alignItems: 'center',
                                                                                                            justifyContent: 'center',
                                                                                                            lineHeight: '1.2',
                                                                                                            minWidth: 'fit-content',
                                                                                                            boxShadow: '0 1px 3px rgba(0, 0, 0, 0.15)',
                                                                                                            border: 'none',
                                                                                                            outline: 'none',
                                                                                                            opacity: 1,
                                                                                                            zIndex: 1
                                                                                                        }}
                                                                                                    >
                                                                                                        {availability.text}
                                                                                                    </span>
                                                                                                )}
                                                                                            </button>
                                                                                        );
                                                                                    })}
                                                                                </div>
                                                                            </div>
                                                                        );
                                                                    })()}

                                                                    {Object.entries(slotsByDay).map(([dayOrDate, timings]: any) => {
                                                                        if (dayOrDate === 'Common slots') return null;
                                                                        if (!getFilteredDays(dayOrDate)) return null;

                                                                        // Deduplicate timings by key
                                                                        const uniqueTimings = Array.from(new Map(timings.map((timing: any) => [timing.key, timing])).values());

                                                                        return (
                                                                            <div key={dayOrDate} className="book-slots-day-timings">
                                                                                <div className="book-slots-day-header-small">
                                                                                    {dayOrDate.toLowerCase().split(' ').map((word: string) => word.charAt(0).toUpperCase() + word.slice(1)).join(' ')}
                                                                                </div>
                                                                                <div className="book-slots-timings-grid">
                                                                                    {uniqueTimings.map((timing: any) => {
                                                                                        const isSelected = selectedTimings[activityId]?.[dayOrDate]?.includes(timing.key);
                                                                                        const availability = getAvailabilityPerSlot(timing, activityId, dayOrDate);

                                                                                        return (
                                                                                            <button
                                                                                                key={timing.key}
                                                                                                onClick={() => {
                                                                                                    if (readOnly) return;
                                                                                                    const actKey = activityId ?? "default";
                                                                                                    const updatedTimings = { ...selectedTimings };
                                                                                                    if (!updatedTimings[actKey]) {
                                                                                                        updatedTimings[actKey] = {};
                                                                                                    }
                                                                                                    const currentSelection = updatedTimings[actKey][dayOrDate];
                                                                                                    const isMultiple = !!selectedBatch?.allowMultipleTimeSlots;

                                                                                                    if (Array.isArray(currentSelection)) {
                                                                                                        if (currentSelection.includes(timing.key)) {
                                                                                                            // Removing a timing - always allowed
                                                                                                            updatedTimings[actKey][dayOrDate] = currentSelection.filter(
                                                                                                                (id: string) => id !== timing.key
                                                                                                            );
                                                                                                        } else {
                                                                                                            // Adding a new timing - check if multiple slots are allowed
                                                                                                            if (!isMultiple) {
                                                                                                                // Check if there are any booked slots for the affected dates
                                                                                                                const affectedDates = getAffectedDates(dayOrDate);
                                                                                                                const bookedCheck = hasBookedSlotsForDates(affectedDates);
                                                                                                                if (bookedCheck.hasBooked) {
                                                                                                                    const isRecurring = selectedBatch?.type === "recurring";
                                                                                                                    const bookedDateStr = bookedCheck.bookedDate
                                                                                                                        ? (isRecurring
                                                                                                                            ? timeutil.getInstance(bookedCheck.bookedDate).format("dddd")
                                                                                                                            : timeutil.getInstance(bookedCheck.bookedDate).format("MMM DD, YYYY"))
                                                                                                                        : "";
                                                                                                                    notification("error", formatMsg("bookSlots.removeExistingBookingFirst", {
                                                                                                                        date: bookedDateStr
                                                                                                                    }) || `Please remove the existing booking for ${bookedDateStr} before selecting a different slot.`);
                                                                                                                    return;
                                                                                                                }

                                                                                                                // dayOrDate is already in the correct format:
                                                                                                                // - For recurring: "MONDAY", "TUESDAY", etc. (uppercase day name)
                                                                                                                // - For non-recurring: formatted date string
                                                                                                                // Remove this day from ALL activities in updatedTimings
                                                                                                                Object.keys(updatedTimings).forEach(actKeyToRemove => {
                                                                                                                    const activitySelection = updatedTimings[actKeyToRemove];
                                                                                                                    if (!activitySelection) return;

                                                                                                                    // Remove the dayOrDate key from this activity
                                                                                                                    if (activitySelection[dayOrDate]) {
                                                                                                                        delete activitySelection[dayOrDate];
                                                                                                                    }
                                                                                                                });

                                                                                                                // Clean up empty activity objects
                                                                                                                Object.keys(updatedTimings).forEach(actKey => {
                                                                                                                    if (Object.keys(updatedTimings[actKey]).length === 0) {
                                                                                                                        delete updatedTimings[actKey];
                                                                                                                    }
                                                                                                                });
                                                                                                            }

                                                                                                            // Check if it would exceed limit
                                                                                                            // Skip limit check if unlimitedClasses is true
                                                                                                            if (!noTotalCap) {
                                                                                                                const estimatedNewSlots = estimateNewSlotsForTiming(dayOrDate, timing.key, activityId);

                                                                                                                // Check if adding this timing would exceed the limit
                                                                                                                const currentTotal = totalBooked + newSelections.length;
                                                                                                                if (currentTotal + estimatedNewSlots > totalClasses) {
                                                                                                                    notification("error", formatMsg("bookSlots.cannotSelectMoreSlotsExceedLimit", {
                                                                                                                        totalBooked: totalBooked,
                                                                                                                        newSelections: newSelections.length,
                                                                                                                        totalClasses: totalClasses
                                                                                                                    }));
                                                                                                                    return;
                                                                                                                }

                                                                                                                if (remainingSlots <= 0 && currentSelection.length === 0) {
                                                                                                                    notification("error", formatMsg("bookSlots.cannotSelectMoreSlotsLimitReached", {
                                                                                                                        totalClasses: totalClasses
                                                                                                                    }));
                                                                                                                    return;
                                                                                                                }
                                                                                                            }

                                                                                                            // Allow selection if unlimitedClasses is true or if within limit
                                                                                                            // Ensure actKey exists (might have been deleted during cleanup)
                                                                                                            if (!updatedTimings[actKey]) {
                                                                                                                updatedTimings[actKey] = {};
                                                                                                            }
                                                                                                            // If multiple slots are allowed, append to existing array; otherwise replace
                                                                                                            if (isMultiple) {
                                                                                                                updatedTimings[actKey][dayOrDate] = [...currentSelection, timing.key];
                                                                                                            } else {
                                                                                                                // After removing from all activities, currentSelection might be undefined
                                                                                                                // So we start fresh with just the new timing
                                                                                                                updatedTimings[actKey][dayOrDate] = [timing.key];
                                                                                                            }
                                                                                                        }
                                                                                                    } else {
                                                                                                        // Adding first timing for this day/date
                                                                                                        // Check if multiple slots are allowed
                                                                                                        if (!isMultiple) {
                                                                                                            // Check if there are any booked slots for the affected dates
                                                                                                            const affectedDates = getAffectedDates(dayOrDate);
                                                                                                            const bookedCheck = hasBookedSlotsForDates(affectedDates);
                                                                                                            if (bookedCheck.hasBooked) {
                                                                                                                const isRecurring = selectedBatch?.type === "recurring";
                                                                                                                const bookedDateStr = bookedCheck.bookedDate
                                                                                                                    ? (isRecurring
                                                                                                                        ? timeutil.getInstance(bookedCheck.bookedDate).format("dddd")
                                                                                                                        : timeutil.getInstance(bookedCheck.bookedDate).format("MMM DD, YYYY"))
                                                                                                                    : "";
                                                                                                                notification("error", formatMsg("bookSlots.removeExistingBookingFirst", {
                                                                                                                    date: bookedDateStr
                                                                                                                }) || `Please remove the existing booking for ${bookedDateStr} before selecting a different slot.`);
                                                                                                                return;
                                                                                                            }

                                                                                                            // dayOrDate is already in the correct format:
                                                                                                            // - For recurring: "MONDAY", "TUESDAY", etc. (uppercase day name)
                                                                                                            // - For non-recurring: formatted date string
                                                                                                            // Remove this day from ALL activities in updatedTimings
                                                                                                            Object.keys(updatedTimings).forEach(actKeyToRemove => {
                                                                                                                const activitySelection = updatedTimings[actKeyToRemove];
                                                                                                                if (!activitySelection) return;

                                                                                                                // Remove the dayOrDate key from this activity
                                                                                                                if (activitySelection[dayOrDate]) {
                                                                                                                    delete activitySelection[dayOrDate];
                                                                                                                }
                                                                                                            });

                                                                                                            // Clean up empty activity objects
                                                                                                            Object.keys(updatedTimings).forEach(actKey => {
                                                                                                                if (Object.keys(updatedTimings[actKey]).length === 0) {
                                                                                                                    delete updatedTimings[actKey];
                                                                                                                }
                                                                                                            });
                                                                                                        }

                                                                                                        // Check limit
                                                                                                        // Skip limit check if unlimitedClasses is true
                                                                                                        if (!noTotalCap) {
                                                                                                            const estimatedNewSlots = estimateNewSlotsForTiming(dayOrDate, timing.key, activityId);

                                                                                                            // Check if adding this timing would exceed the limit
                                                                                                            const currentTotal = totalBooked + newSelections.length;
                                                                                                            if (currentTotal + estimatedNewSlots > totalClasses) {
                                                                                                                notification("error", formatMsg("bookSlots.cannotSelectMoreSlotsExceedLimit", {
                                                                                                                    totalBooked: totalBooked,
                                                                                                                    newSelections: newSelections.length,
                                                                                                                    totalClasses: totalClasses
                                                                                                                }));
                                                                                                                return;
                                                                                                            }

                                                                                                            if (remainingSlots <= 0) {
                                                                                                                notification("error", formatMsg("bookSlots.cannotSelectMoreSlotsLimitReached", {
                                                                                                                    totalClasses: totalClasses
                                                                                                                }));
                                                                                                                return;
                                                                                                            }
                                                                                                        }

                                                                                                        // Allow selection if unlimitedClasses is true or if within limit
                                                                                                        // Ensure actKey exists (might have been deleted during cleanup)
                                                                                                        if (!updatedTimings[actKey]) {
                                                                                                            updatedTimings[actKey] = {};
                                                                                                        }
                                                                                                        updatedTimings[actKey][dayOrDate] = [timing.key];
                                                                                                    }
                                                                                                    setSelectedTimings(updatedTimings);
                                                                                                }}
                                                                                                className={`book-slots-timing-button ${isSelected ? 'selected' : ''}`}
                                                                                                disabled={readOnly || (!selectedDays.length && !isSelected) || availability.text === "Sold out"}
                                                                                            >
                                                                                                <span style={{ flex: 1 }}>{timing.label}</span>
                                                                                                {availability.text && (
                                                                                                    <span
                                                                                                        className="book-slots-availability-badge"
                                                                                                        style={{
                                                                                                            color: getAvailabilityFontColor(availability),
                                                                                                            fontSize: '11px',
                                                                                                            fontWeight: 600,
                                                                                                            whiteSpace: 'nowrap',
                                                                                                            backgroundColor: isSelected
                                                                                                                ? (availability.color === '#8D8D8D' ? '#8D8D8D' : availability.color)
                                                                                                                : (availability.color || '#8D8D8D'),
                                                                                                            padding: '4px 10px',
                                                                                                            borderRadius: '12px',
                                                                                                            display: 'inline-flex',
                                                                                                            alignItems: 'center',
                                                                                                            justifyContent: 'center',
                                                                                                            lineHeight: '1.2',
                                                                                                            minWidth: 'fit-content',
                                                                                                            boxShadow: '0 1px 3px rgba(0, 0, 0, 0.15)',
                                                                                                            border: 'none',
                                                                                                            outline: 'none',
                                                                                                            opacity: 1,
                                                                                                            zIndex: 1
                                                                                                        }}
                                                                                                    >
                                                                                                        {availability.text}
                                                                                                    </span>
                                                                                                )}
                                                                                            </button>
                                                                                        );
                                                                                    })}
                                                                                </div>
                                                                            </div>
                                                                        );
                                                                    })}
                                                                </div>
                                                            );
                                                        })
                                                    )}
                                                </div>
                                            </div>
                                        </div>
                                    ) : (
                                        <div className="book-slots-calendar-placeholder">
                                            <p>{formatMsg("calendarComponentHere")}</p>
                                        </div>
                                    )}
                                </div>
                            )}
                        </div>
                    </Col>
                </Row>
            </div>
        </ModalWrapper>
    );
};

export default BookSlotsModal;

